import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Input,
  Select,
  InputNumber,
  Modal,
  Dropdown,
  Checkbox,
  Spin,
  Button,
  Tooltip,
} from "antd";
import type { MenuProps } from "antd";
import {
  PlusOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  CameraOutlined,
  DownOutlined,
  RightOutlined,
  CheckOutlined,
  AppstoreOutlined,
  TeamOutlined,
  HeartOutlined,
  ReadOutlined,
  ExperimentOutlined,
  SearchOutlined,
  SettingOutlined,
  EyeOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useGate } from "../Subscription/entitlements";
import { getCurrentRole, canManageUsers } from "../../utils/roles";
import PublicBalancedScorecardHistoryShareModal from "./PublicBalancedScorecardHistoryShareModal";
import type {
  ApiScorecardFull,
  ApiDomainWithIndicators,
  ApiIndicator,
  TrafficLight,
  UpdateMode,
  ValueSource,
  ValueScale,
  BenchmarkSource,
  ApprovalStatus,
  HistoryListResponse,
  HistorySnapshotFull,
  CompareResponse,
  ApiTemplate,
  DynamicRangeBand,
} from "./balancedScorecard.service";
import {
  getScorecard,
  createScorecard,
  updateScorecard as apiUpdateScorecard,
  publishScorecard as apiPublishScorecard,
  createDomain,
  updateDomain,
  deleteDomain as apiDeleteDomain,
  createIndicator,
  updateIndicator as apiUpdateIndicator,
  deleteIndicator as apiDeleteIndicator,
  setIndicatorApproval,
  updateIndicatorBenchmarks,
  getScorecardHistory,
  getHistorySnapshot,
  compareSnapshots as apiCompareSnapshots,
  getTemplates,
  getLinkableFormulas,
  getBenchmarkLibrary,
  listDimensions,
  getBreakdown,
} from "./balancedScorecard.service";
import type {
  LinkableFormula,
  ApiBenchmarkLibraryEntry,
  ApiDimension,
  BreakdownResponse,
} from "./balancedScorecard.service";
import IndicatorBreakdown from "./IndicatorBreakdown";
import ManageDimensionsModal from "./ManageDimensionsModal";
import ScorecardSettingsModal from "./ScorecardSettingsModal";
import BulkEntryModal from "./BulkEntryModal";
import "./BalancedScorecardPage.css";
import "./BalancedScorecardHistory.css";
import "./BalancedScorecardBreakdown.css";

// ── Constants ─────────────────────────────────────────────────────────────────

const TRAFFIC_CONFIG: Record<
  string,
  { labelKey: string; color: string; bg: string }
> = {
  green: { labelKey: "trafficLight.onTrack", color: "#16a34a", bg: "#dcfce7" },
  yellow: { labelKey: "trafficLight.atRisk", color: "#d97706", bg: "#fef3c7" },
  red: { labelKey: "trafficLight.critical", color: "#dc2626", bg: "#fee2e2" },
};

function tlConf(tl: TrafficLight, t: TFunction) {
  const conf = TRAFFIC_CONFIG[tl ?? ""] ?? {
    labelKey: "trafficLight.noData",
    color: "#6b7280",
    bg: "#f3f4f6",
  };
  return { label: t(conf.labelKey), color: conf.color, bg: conf.bg };
}

function scoreColor(score: number | null): string {
  if (score === null) return "#6b7280";
  if (score >= 80) return "#16a34a";
  if (score >= 50) return "#d97706";
  return "#dc2626";
}

function scoreBackground(score: number | null): string {
  if (score === null) return "#f3f4f6";
  if (score >= 80) return "#dcfce7";
  if (score >= 50) return "#fef3c7";
  return "#fee2e2";
}

const DOMAIN_COLORS = [
  "#10b981",
  "#f59e0b",
  "#3b82f6",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
];

function getDefaultDynamicRange(t: TFunction): DynamicRangeBand[] {
  return [
    {
      color: "#ef4444",
      from: 0,
      to: 20,
      label: t("indicatorModal.defaultBands.deficient"),
      description: "",
    },
    {
      color: "#f97316",
      from: 21,
      to: 40,
      label: t("indicatorModal.defaultBands.insufficient"),
      description: "",
    },
    {
      color: "#eab308",
      from: 41,
      to: 60,
      label: t("indicatorModal.defaultBands.satisfactory"),
      description: "",
    },
    {
      color: "#86efac",
      from: 61,
      to: 80,
      label: t("indicatorModal.defaultBands.veryGood"),
      description: "",
    },
    {
      color: "#16a34a",
      from: 81,
      to: 100,
      label: t("indicatorModal.defaultBands.excellent"),
      description: "",
    },
  ];
}

function getValueScaleOptions(t: TFunction) {
  return [
    { value: "percentage", label: t("indicatorModal.valueScales.percentage") },
    { value: "score_0_10", label: t("indicatorModal.valueScales.score010") },
    { value: "score_1_5", label: t("indicatorModal.valueScales.score15") },
    { value: "count", label: t("indicatorModal.valueScales.count") },
    { value: "custom", label: t("indicatorModal.valueScales.custom") },
  ];
}

const FALLBACK_TEMPLATES: ApiTemplate[] = [
  {
    id: "__kaplan__",
    name: "Kaplan-Norton (Classic)",
    best_for:
      "The original four-perspective model: Financial, Customer, Internal Processes, Learning & Growth.",
    domain_count: 4,
  },
  {
    id: "__ngo__",
    name: "NGO / Development",
    best_for:
      "Beneficiary impact, programme delivery, sustainability, organisational health.",
    domain_count: 4,
  },
  {
    id: "__healthcare__",
    name: "Healthcare",
    best_for:
      "Patient outcomes, satisfaction, operational efficiency, staff development.",
    domain_count: 4,
  },
  {
    id: "__education__",
    name: "Education",
    best_for:
      "Student outcomes, equity, teaching quality, institutional capacity.",
    domain_count: 4,
  },
  {
    id: "__research__",
    name: "Research / Academic",
    best_for:
      "Research output, funding health, training, knowledge translation.",
    domain_count: 4,
  },
];

interface LocalTemplateDomain {
  name: string;
  weight: number;
  color: string;
  indicators: Array<{ name: string; weight: number; value_scale: ValueScale }>;
}

const LOCAL_TEMPLATE_DATA: Record<string, LocalTemplateDomain[]> = {
  __kaplan__: [
    {
      name: "Financial",
      weight: 30,
      color: "#10b981",
      indicators: [
        { name: "Revenue growth rate", weight: 35, value_scale: "percentage" },
        { name: "Net profit margin", weight: 35, value_scale: "percentage" },
        {
          name: "Return on investment (ROI)",
          weight: 30,
          value_scale: "percentage",
        },
      ],
    },
    {
      name: "Customer",
      weight: 25,
      color: "#f59e0b",
      indicators: [
        {
          name: "Customer satisfaction score (CSAT)",
          weight: 35,
          value_scale: "score_1_5",
        },
        {
          name: "Customer retention rate",
          weight: 35,
          value_scale: "percentage",
        },
        {
          name: "Net promoter score (NPS)",
          weight: 30,
          value_scale: "score_0_10",
        },
      ],
    },
    {
      name: "Internal Processes",
      weight: 25,
      color: "#3b82f6",
      indicators: [
        {
          name: "Process efficiency rate",
          weight: 35,
          value_scale: "percentage",
        },
        { name: "Quality defect rate", weight: 35, value_scale: "percentage" },
        {
          name: "On-time delivery rate",
          weight: 30,
          value_scale: "percentage",
        },
      ],
    },
    {
      name: "Learning & Growth",
      weight: 20,
      color: "#8b5cf6",
      indicators: [
        {
          name: "Employee satisfaction score",
          weight: 35,
          value_scale: "score_1_5",
        },
        {
          name: "Training completion rate",
          weight: 35,
          value_scale: "percentage",
        },
        { name: "Innovation index", weight: 30, value_scale: "score_0_10" },
      ],
    },
  ],
  __ngo__: [
    {
      name: "Impact & Outcomes",
      weight: 30,
      color: "#10b981",
      indicators: [
        { name: "Beneficiaries reached", weight: 35, value_scale: "count" },
        {
          name: "Outcome achievement rate",
          weight: 35,
          value_scale: "percentage",
        },
        {
          name: "Beneficiary satisfaction score",
          weight: 30,
          value_scale: "score_1_5",
        },
      ],
    },
    {
      name: "Programme Delivery",
      weight: 25,
      color: "#f59e0b",
      indicators: [
        {
          name: "Project completion rate",
          weight: 40,
          value_scale: "percentage",
        },
        {
          name: "Report submission timeliness",
          weight: 30,
          value_scale: "percentage",
        },
        {
          name: "Staff utilization rate",
          weight: 30,
          value_scale: "percentage",
        },
      ],
    },
    {
      name: "Sustainability & Finance",
      weight: 25,
      color: "#3b82f6",
      indicators: [
        {
          name: "Budget utilization rate",
          weight: 40,
          value_scale: "percentage",
        },
        { name: "Cost per beneficiary", weight: 30, value_scale: "custom" },
        { name: "Donor retention rate", weight: 30, value_scale: "percentage" },
      ],
    },
    {
      name: "Organisational Health",
      weight: 20,
      color: "#8b5cf6",
      indicators: [
        { name: "Active partnerships", weight: 50, value_scale: "count" },
        {
          name: "Stakeholder engagement score",
          weight: 50,
          value_scale: "score_1_5",
        },
      ],
    },
  ],
  __healthcare__: [
    {
      name: "Patient Outcomes",
      weight: 35,
      color: "#ef4444",
      indicators: [
        {
          name: "Treatment success rate",
          weight: 40,
          value_scale: "percentage",
        },
        {
          name: "Patient readmission rate",
          weight: 30,
          value_scale: "percentage",
        },
        { name: "Average length of stay", weight: 30, value_scale: "count" },
      ],
    },
    {
      name: "Patient Satisfaction",
      weight: 25,
      color: "#f59e0b",
      indicators: [
        {
          name: "Patient satisfaction score",
          weight: 50,
          value_scale: "score_1_5",
        },
        {
          name: "Complaint resolution rate",
          weight: 50,
          value_scale: "percentage",
        },
      ],
    },
    {
      name: "Operational Efficiency",
      weight: 25,
      color: "#3b82f6",
      indicators: [
        { name: "Bed occupancy rate", weight: 35, value_scale: "percentage" },
        {
          name: "Average wait time (minutes)",
          weight: 35,
          value_scale: "count",
        },
        { name: "Cost per patient", weight: 30, value_scale: "custom" },
      ],
    },
    {
      name: "Staff Development",
      weight: 15,
      color: "#10b981",
      indicators: [
        {
          name: "Staff satisfaction score",
          weight: 50,
          value_scale: "score_1_5",
        },
        {
          name: "Training completion rate",
          weight: 50,
          value_scale: "percentage",
        },
      ],
    },
  ],
  __education__: [
    {
      name: "Student Outcomes",
      weight: 35,
      color: "#10b981",
      indicators: [
        { name: "Graduation rate", weight: 35, value_scale: "percentage" },
        {
          name: "Average assessment score",
          weight: 35,
          value_scale: "score_0_10",
        },
        {
          name: "Student progression rate",
          weight: 30,
          value_scale: "percentage",
        },
      ],
    },
    {
      name: "Equity & Access",
      weight: 25,
      color: "#f59e0b",
      indicators: [
        { name: "Enrolment rate", weight: 50, value_scale: "percentage" },
        {
          name: "Scholarship coverage rate",
          weight: 50,
          value_scale: "percentage",
        },
      ],
    },
    {
      name: "Teaching Quality",
      weight: 25,
      color: "#3b82f6",
      indicators: [
        {
          name: "Teacher qualification rate",
          weight: 40,
          value_scale: "percentage",
        },
        {
          name: "Student satisfaction score",
          weight: 30,
          value_scale: "score_1_5",
        },
        {
          name: "Curriculum delivery rate",
          weight: 30,
          value_scale: "percentage",
        },
      ],
    },
    {
      name: "Institutional Capacity",
      weight: 15,
      color: "#8b5cf6",
      indicators: [
        {
          name: "Infrastructure utilization rate",
          weight: 50,
          value_scale: "percentage",
        },
        { name: "Staff-to-student ratio", weight: 50, value_scale: "custom" },
      ],
    },
  ],
  __research__: [
    {
      name: "Research Output",
      weight: 35,
      color: "#8b5cf6",
      indicators: [
        { name: "Publications per year", weight: 40, value_scale: "count" },
        { name: "Citation index", weight: 35, value_scale: "score_0_10" },
        {
          name: "Research project completion rate",
          weight: 25,
          value_scale: "percentage",
        },
      ],
    },
    {
      name: "Funding Health",
      weight: 25,
      color: "#10b981",
      indicators: [
        { name: "Grant success rate", weight: 50, value_scale: "percentage" },
        {
          name: "Budget utilization rate",
          weight: 50,
          value_scale: "percentage",
        },
      ],
    },
    {
      name: "Training & Capacity",
      weight: 25,
      color: "#f59e0b",
      indicators: [
        { name: "Researchers trained", weight: 50, value_scale: "count" },
        {
          name: "Training completion rate",
          weight: 50,
          value_scale: "percentage",
        },
      ],
    },
    {
      name: "Knowledge Translation",
      weight: 15,
      color: "#3b82f6",
      indicators: [
        { name: "Policy briefs produced", weight: 50, value_scale: "count" },
        {
          name: "Stakeholder engagement score",
          weight: 50,
          value_scale: "score_1_5",
        },
      ],
    },
  ],
};

// ── Component ─────────────────────────────────────────────────────────────────

// Wraps a control that only Owners and Managers can use. When the viewer
// lacks that role, the control is disabled and hovering it explains why.
const ManagerOnlyTip: React.FC<{ show: boolean; children: React.ReactNode }> = ({
  show,
  children,
}) => {
  const { t } = useTranslation("balancedScorecard");
  if (!show) return <>{children}</>;
  return (
    <Tooltip title={t("permissions.managerOnly")}>
      <span className="bsc-guard-wrap">{children}</span>
    </Tooltip>
  );
};

// Breakdown cell text → number. "" = no data (null). Anything else must be a number.
const parseBreakdownText = (text: string): number | null | undefined => {
  const s = text.trim();
  if (s === "") return null;
  if (!/^-?\d+(\.\d+)?$/.test(s)) return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
};

const BalancedScorecardPage: React.FC = () => {
  const { t } = useTranslation("balancedScorecard");
  const { id: projectId, scorecardId } = useParams<{
    id: string;
    scorecardId: string;
  }>();
  const navigate = useNavigate();
  const gate = useGate();

  // ── Core state
  const [scorecard, setScorecard] = useState<ApiScorecardFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<HistoryListResponse | null>(null);
  const [histLoading, setHistLoading] = useState(false);
  const [savingTitle, setSavingTitle] = useState(false);
  const [publishingScorecard, setPublishingScorecard] = useState(false);
  const [creatingScorecard, setCreatingScorecard] = useState(false);
  const [savingDomain, setSavingDomain] = useState(false);
  const [savingIndicator, setSavingIndicator] = useState(false);
  const [approvingIndicator, setApprovingIndicator] = useState(false);
  const [deletingItem, setDeletingItem] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "scorecard" | "indicators" | "benchmarks" | "history"
  >("scorecard");
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(
    new Set(),
  );
  const [expandedIndicators, setExpandedIndicators] = useState<Set<string>>(
    new Set(),
  );
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleInput, setTitleInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [filterDomainId, setFilterDomainId] = useState<string | undefined>(
    undefined,
  );
  const [filterStatus, setFilterStatus] = useState<string | undefined>(
    undefined,
  );

  // ── Dimensions (disaggregation)
  const [dimensions, setDimensions] = useState<ApiDimension[]>([]);
  const [manageDimOpen, setManageDimOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  // "Breakdown" dropdown on the Overall weight row. null = no breakdown.
  const [breakdownDimId, setBreakdownDimId] = useState<string | null>(null);
  const [breakdownData, setBreakdownData] = useState<BreakdownResponse | null>(
    null,
  );
  // Bumped after Bulk Entry saves so the breakdown reloads.
  const [breakdownRefresh, setBreakdownRefresh] = useState(0);

  useEffect(() => {
    if (!breakdownDimId || !scorecardId) {
      setBreakdownData(null);
      return;
    }
    let cancelled = false;
    getBreakdown(scorecardId, breakdownDimId)
      .then((res) => !cancelled && setBreakdownData(res))
      .catch(() => {
        if (!cancelled) {
          setBreakdownData(null);
          toast.error(t("breakdown.loadFailed"));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [breakdownDimId, scorecardId, breakdownRefresh]);

  // If the selected dimension is deleted, fall back to "No breakdown".
  useEffect(() => {
    if (breakdownDimId && !dimensions.some((d) => d.id === breakdownDimId)) {
      setBreakdownDimId(null);
    }
  }, [dimensions, breakdownDimId]);
  const loadDimensions = useCallback(async () => {
    if (!scorecardId) return;
    try {
      setDimensions(await listDimensions(scorecardId));
    } catch {
      // Keep the rest of the page usable if the dimensions endpoint fails.
      setDimensions([]);
    }
  }, [scorecardId]);

  useEffect(() => {
    loadDimensions();
  }, [loadDimensions]);

  // ── Modals
  const [unsavedChangesModal, setUnsavedChangesModal] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [templateTitle, setTemplateTitle] = useState<string>("");
  const [templateTitleError, setTemplateTitleError] = useState<string>("");
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [pinToDashboard, setPinToDashboard] = useState(
    () => localStorage.getItem(`bsc_pinned_${projectId}`) === "1",
  );

  const togglePinToDashboard = (checked: boolean) => {
    setPinToDashboard(checked);
    if (checked) {
      localStorage.setItem(`bsc_pinned_${projectId}`, "1");
    } else {
      localStorage.removeItem(`bsc_pinned_${projectId}`);
    }
  };
  const [deleteTarget, setDeleteTarget] = useState<{
    type: "domain" | "indicator";
    id: string;
    domainId?: string;
    label: string;
  } | null>(null);

  // ── Templates / formulas / benchmarks
  const [templates, setTemplates] = useState<ApiTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [linkableFormulas, setLinkableFormulas] = useState<LinkableFormula[]>(
    [],
  );
  const [benchmarkLibrary, setBenchmarkLibrary] = useState<
    ApiBenchmarkLibraryEntry[]
  >([]);

  // ── History
  const [previewPub, setPreviewPub] = useState<HistorySnapshotFull | null>(
    null,
  );
  // Id of the version whose preview is loading. Only that row shows a spinner.
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const [compareLeft, setCompareLeft] = useState<string | undefined>(undefined);
  const [compareRight, setCompareRight] = useState<string | undefined>(
    undefined,
  );
  const [compareResult, setCompareResult] = useState<CompareResponse | null>(
    null,
  );
  const [shareModalOpen, setShareModalOpen] = useState(false);
  // Version ids (version_number) hidden from the History chart and table.
  const [hiddenVersions, setHiddenVersions] = useState<Set<number>>(new Set());
  const [openCompareDomains, setOpenCompareDomains] = useState<Set<string>>(
    new Set(),
  );

  // ── Approval modal
  const [approvalModal, setApprovalModal] = useState<{
    open: boolean;
    indicatorId: string;
    domainId: string;
    indicatorName: string;
    status: "accepted" | "rejected";
    note: string;
  } | null>(null);

  // ── Benchmarks inline edit: indicatorId → { lower, upper, target, saving }
  const [benchmarkEdits, setBenchmarkEdits] = useState<
    Record<
      string,
      {
        lower: number | null;
        upper: number | null;
        target: number | null;
        saving: boolean;
      }
    >
  >({});

  // ── Domain modal
  const [domainModal, setDomainModal] = useState<{
    open: boolean;
    mode: "add" | "edit";
    id?: string;
    name: string;
    color: string;
    weight: number;
  }>({
    open: false,
    mode: "add",
    name: "",
    color: DOMAIN_COLORS[0],
    weight: 25,
  });

  // ── Indicator modal
  // Structural edits (domains, indicators, publish, dimensions) need Owner/Manager.
  const canWrite = canManageUsers(getCurrentRole());

  // Disaggregation section in the indicator drawer; collapsed by default.
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [indicatorModal, setIndicatorModal] = useState<{
    open: boolean;
    mode: "add" | "edit";
    domainId: string;
    id?: string;
    name: string;
    description: string;
    weight: number;
    value_scale: ValueScale | "";
    value_source: "formula" | "manual";
    linked_formula: string;
    update_mode: UpdateMode;
    current_value: string;
    unit: string;
    benchmark_source: BenchmarkSource;
    lower_threshold: number | null;
    upper_threshold: number | null;
    target: number | null;
    apply_to_domain: boolean;
    score_bands: DynamicRangeBand[];
    normalised_score: number | null;
    traffic_light: TrafficLight;
    // Disaggregation: tagged dimension ids, cell text per dimension/category,
    // and the tags the indicator had when it was opened (for the untag prompt).
    breakdown_dims: string[];
    breakdown_values: Record<string, Record<string, string>>;
    original_breakdown_dims: string[];
  }>({
    open: false,
    mode: "add",
    domainId: "",
    name: "",
    description: "",
    weight: 25,
    value_scale: "",
    value_source: "manual",
    linked_formula: "",
    update_mode: "manual",
    current_value: "",
    unit: "",
    benchmark_source: "manual",
    lower_threshold: null,
    upper_threshold: null,
    target: null,
    apply_to_domain: false,
    score_bands: getDefaultDynamicRange(t).map((b) => ({ ...b })),
    normalised_score: null,
    traffic_light: null,
    breakdown_dims: [],
    breakdown_values: {},
    original_breakdown_dims: [],
  });

  // ── Load scorecard ────────────────────────────────────────────────────────

  const loadScorecard = useCallback(async () => {
    if (!scorecardId) return;
    if (scorecardId === "new") {
      setLoading(false);
      setTemplateTitle("");
      setTemplateTitleError("");
      setSelectedTemplate("");
      setTemplateModalOpen(true);
      return;
    }
    try {
      const sc = await getScorecard(scorecardId);
      setScorecard(sc);
      if (sc) {
        setTitleInput(sc.title);
        setExpandedDomains(new Set(sc.domains.map((d) => d.id)));
      }
    } catch {
      toast.error(t("toasts.loadScorecardFailed"));
    } finally {
      setLoading(false);
    }
  }, [scorecardId, t]);

  const loadHistory = useCallback(async (scorecardId: string) => {
    setHistLoading(true);
    try {
      const h = await getScorecardHistory(scorecardId);
      setHistory(h);
    } catch {
      // history may be empty for new scorecards
      setHistory(null);
    } finally {
      setHistLoading(false);
    }
  }, []);

  const handleApproval = async () => {
    if (!approvalModal) return;
    setApprovingIndicator(true);
    try {
      await setIndicatorApproval(
        approvalModal.domainId,
        approvalModal.indicatorId,
        {
          approval_status: approvalModal.status as ApprovalStatus,
          approval_note: approvalModal.note.trim() || undefined,
        },
      );
      setApprovalModal(null);
      toast.success(
        approvalModal.status === "accepted"
          ? t("toasts.indicatorApproved")
          : t("toasts.indicatorRejected"),
      );
      await loadScorecard();
    } catch {
      toast.error(t("toasts.approvalUpdateFailed"));
    } finally {
      setApprovingIndicator(false);
    }
  };

  const saveBenchmark = async (ind: ApiIndicator) => {
    const edit = benchmarkEdits[ind.id];
    if (!edit) return;
    setBenchmarkEdits((prev) => ({
      ...prev,
      [ind.id]: { ...prev[ind.id], saving: true },
    }));
    try {
      await updateIndicatorBenchmarks(ind.id, {
        benchmark_lower: edit.lower,
        benchmark_upper: edit.upper,
        target_value: edit.target,
      });
      toast.success(t("toasts.benchmarksSaved"));
      setBenchmarkEdits((prev) => {
        const next = { ...prev };
        delete next[ind.id];
        return next;
      });
      await loadScorecard();
    } catch {
      toast.error(t("toasts.benchmarksSaveFailed"));
      setBenchmarkEdits((prev) => ({
        ...prev,
        [ind.id]: { ...prev[ind.id], saving: false },
      }));
    }
  };

  useEffect(() => {
    loadScorecard();
  }, [loadScorecard]);

  useEffect(() => {
    if (scorecard && activeTab === "history") {
      loadHistory(scorecard.id);
    }
  }, [activeTab, scorecard?.id]);

  // ── Compare effect
  useEffect(() => {
    if (!compareLeft || !compareRight || !scorecard) {
      setCompareResult(null);
      return;
    }
    apiCompareSnapshots(scorecard.id, compareLeft, compareRight)
      .then(setCompareResult)
      .catch(() => setCompareResult(null));
  }, [compareLeft, compareRight]);

  // ── Load templates when modal opens
  useEffect(() => {
    if (!templateModalOpen) return;
    setTemplatesLoading(true);
    getTemplates()
      .then((t) => {
        const list = t.length > 0 ? t : FALLBACK_TEMPLATES;
        setTemplates(list);
        if (!selectedTemplate) setSelectedTemplate(list[0].id);
      })
      .catch(() => {
        setTemplates(FALLBACK_TEMPLATES);
        setSelectedTemplate(FALLBACK_TEMPLATES[0].id);
      })
      .finally(() => setTemplatesLoading(false));
  }, [templateModalOpen]);

  // ── Load formulas/benchmarks when indicator modal opens
  useEffect(() => {
    if (!indicatorModal.open || !scorecard) return;
    getLinkableFormulas(scorecard.id)
      .then(setLinkableFormulas)
      .catch(() => {});
    getBenchmarkLibrary(scorecard.id)
      .then(setBenchmarkLibrary)
      .catch(() => {});
  }, [indicatorModal.open, scorecard?.id]);

  // ── Create scorecard ──────────────────────────────────────────────────────

  const handleUseTemplate = async () => {
    if (!projectId || !selectedTemplate || !templateTitle.trim()) {
      setTemplateTitleError(t("messages.changesSaved")); // fallback
      return;
    }
    const isLocal = selectedTemplate.startsWith("__");
    setCreatingScorecard(true);

    try {
      setTemplateTitleError("");
      let sc = scorecard;
      if (!sc) {
        sc = await createScorecard(projectId, {
          title: templateTitle.trim(),
          ...(!isLocal && { template_id: selectedTemplate }),
        });
      }

      if (isLocal && sc) {
        const domainDefs = LOCAL_TEMPLATE_DATA[selectedTemplate] ?? [];
        for (const domainDef of domainDefs) {
          const domain = await createDomain(sc.id, {
            name: domainDef.name,
            weight: domainDef.weight,
            color: domainDef.color,
          });
          for (const indDef of domainDef.indicators) {
            await createIndicator(domain.id, {
              name: indDef.name,
              weight: indDef.weight,
              value_scale: indDef.value_scale,
              value_source: "manual",
              benchmark_source: "manual",
            });
          }
        }
      }

      if (scorecardId === "new" && sc) {
        setTemplateModalOpen(false);
        setTemplateTitle("");
        toast.success(t("toasts.scorecardCreatedWithTemplate"));
        navigate(`/balanced-scorecard/${projectId}/${sc.id}`);
      } else {
        await loadScorecard();
        setTemplateModalOpen(false);
        setTemplateTitle("");
        toast.success(t("toasts.scorecardCreatedWithTemplate"));
      }
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setTemplateTitleError("This title already exists. Please choose a different one.");
      } else {
        console.error("error:", err);
        toast.error(
          err?.response?.data?.detail || t("toasts.createScorecardFailed"),
        );
      }
    } finally {
      setCreatingScorecard(false);
    }
  };

  const handleStartFromScratch = async () => {
    if (!projectId) return;
    setCreatingScorecard(true);
    try {
      const sc = await createScorecard(projectId, {
        title: t("common.defaultScorecardTitle"),
      });
      setScorecard(sc);
      setTitleInput(sc.title);
      toast.success(t("toasts.scorecardCreated"));
    } catch (err: any) {
      if (err?.response?.status === 409) {
        toast.error(t("toasts.scorecardAlreadyExists"));
      } else {
        toast.error(
          err?.response?.data?.detail || t("toasts.createScorecardFailed"),
        );
      }
    } finally {
      setCreatingScorecard(false);
    }
  };

  // ── Title ─────────────────────────────────────────────────────────────────

  const handleSaveTitle = async () => {
    if (!titleInput.trim() || !scorecardId) return;
    setSavingTitle(true);
    try {
      await apiUpdateScorecard(scorecardId, { title: titleInput.trim() });
      setScorecard((prev) =>
        prev ? { ...prev, title: titleInput.trim() } : prev,
      );
      setEditingTitle(false);
    } catch {
      toast.error(t("toasts.updateTitleFailed"));
    } finally {
      setSavingTitle(false);
    }
  };

  // ── Domain CRUD ───────────────────────────────────────────────────────────

  const openAddDomain = () => {
    const nextColor =
      DOMAIN_COLORS[(scorecard?.domains.length ?? 0) % DOMAIN_COLORS.length];
    setDomainModal({
      open: true,
      mode: "add",
      name: "",
      color: nextColor,
      weight: 25,
    });
  };

  const openEditDomain = (d: ApiDomainWithIndicators) => {
    setDomainModal({
      open: true,
      mode: "edit",
      id: d.id,
      name: d.name,
      color: d.color,
      weight: d.weight,
    });
  };

  const saveDomain = async () => {
    if (!domainModal.name.trim() || !scorecard) return;
    setSavingDomain(true);
    try {
      if (domainModal.mode === "add") {
        const newDom = await createDomain(scorecard.id, {
          name: domainModal.name.trim(),
          color: domainModal.color,
          weight: domainModal.weight,
        });
        setExpandedDomains((s) => {
          const n = new Set(Array.from(s));
          n.add(newDom.id);
          return n;
        });
        toast.success(t("toasts.domainCreated"));
      } else {
        await updateDomain(scorecard.id, domainModal.id!, {
          name: domainModal.name.trim(),
          color: domainModal.color,
          weight: domainModal.weight,
        });
        toast.success(t("toasts.domainUpdated"));
      }
      setDomainModal((d) => ({ ...d, open: false }));
      await loadScorecard();
    } catch {
      toast.error(t("toasts.domainSaveFailed"));
    } finally {
      setSavingDomain(false);
    }
  };

  // ── Indicator CRUD ────────────────────────────────────────────────────────

  const openAddIndicator = (domainId: string) => {
    const domain = scorecard?.domains.find((d) => d.id === domainId);
    const usedWeight =
      domain?.indicators.reduce((s, i) => s + Number(i.weight), 0) ?? 0;
    const remaining = Math.max(0, 100 - usedWeight);
    setIndicatorModal({
      open: true,
      mode: "add",
      domainId,
      name: "",
      description: "",
      weight: Math.min(25, remaining),
      value_scale: "",
      value_source: "manual",
      linked_formula: "",
      update_mode: "manual",
      current_value: "",
      unit: "",
      benchmark_source: "manual",
      lower_threshold: null,
      upper_threshold: null,
      target: null,
      apply_to_domain: false,
      score_bands: getDefaultDynamicRange(t).map((b) => ({ ...b })),
      normalised_score: null,
      traffic_light: null,
      breakdown_dims: [],
      breakdown_values: {},
      original_breakdown_dims: [],
    });
    // Open straight away when there is something to tag to.
    setBreakdownOpen(dimensions.length > 0);
  };

  const openEditIndicator = (ind: ApiIndicator) => {
    setIndicatorModal({
      open: true,
      mode: "edit",
      domainId: ind.domain_id,
      id: ind.id,
      name: ind.name,
      description: "",
      weight: ind.weight,
      value_scale: ind.value_scale || "",
      value_source: ind.value_source === "formula" ? "formula" : "manual",
      linked_formula: ind.linked_formula_id ?? "",
      update_mode: ind.update_mode,
      current_value:
        ind.current_value !== null ? String(ind.current_value) : "",
      unit: "",
      benchmark_source: ind.benchmark_source,
      lower_threshold: ind.benchmark_lower,
      upper_threshold: ind.benchmark_upper,
      target: ind.target_value,
      apply_to_domain: false,
      score_bands: ind.dynamic_range
        ? ind.dynamic_range.map((b) => ({
            ...b,
            description: b.description ?? "",
          }))
        : getDefaultDynamicRange(t).map((b) => ({ ...b })),
      normalised_score: ind.normalised_score,
      traffic_light: ind.traffic_light,
      breakdown_dims: (ind.breakdowns ?? []).map((b) => b.dimension_id),
      breakdown_values: Object.fromEntries(
        (ind.breakdowns ?? []).map((b) => [
          b.dimension_id,
          Object.fromEntries(
            b.values.map((v) => [
              v.category_id,
              v.value == null ? "" : String(Number(v.value)),
            ]),
          ),
        ]),
      ),
      original_breakdown_dims: (ind.breakdowns ?? []).map(
        (b) => b.dimension_id,
      ),
    });
    setBreakdownOpen((ind.breakdowns ?? []).length > 0);
  };

  // Builds the complete tag list the API expects. Every tagged dimension is
  // sent with all its categories; untagged dimensions are left out, which
  // untags them on the server.
  const buildBreakdowns = (): { dimension_id: string; values: { category_id: string; value: number | null }[] }[] | null => {
    const result: { dimension_id: string; values: { category_id: string; value: number | null }[] }[] = [];
    for (const dimId of indicatorModal.breakdown_dims) {
      const dim = dimensions.find((d) => d.id === dimId);
      if (!dim) continue;
      const cells = indicatorModal.breakdown_values[dimId] ?? {};
      const values: { category_id: string; value: number | null }[] = [];
      for (const c of dim.categories) {
        const parsed = parseBreakdownText(cells[c.id] ?? "");
        if (parsed === undefined) return null; // not a number
        values.push({ category_id: c.id, value: parsed });
      }
      result.push({ dimension_id: dimId, values });
    }
    return result;
  };

  const saveIndicator = async () => {
    if (!indicatorModal.name.trim()) return;
    const breakdowns = buildBreakdowns();
    if (breakdowns === null) {
      toast.error(t("disaggregation.invalidValue"));
      return;
    }
    setSavingIndicator(true);
    const valueSource: ValueSource = indicatorModal.value_source;
    const currentVal = indicatorModal.current_value.trim()
      ? parseFloat(indicatorModal.current_value) || null
      : null;

    const payload = {
      name: indicatorModal.name.trim(),
      value_source: valueSource,
      linked_formula_id:
        valueSource === "formula"
          ? indicatorModal.linked_formula || null
          : null,
      current_value: valueSource === "manual" ? currentVal : null,
      value_scale: (indicatorModal.value_scale || "percentage") as ValueScale,
      weight: indicatorModal.weight,
      target_value: indicatorModal.target,
      benchmark_upper: indicatorModal.upper_threshold,
      benchmark_lower: indicatorModal.lower_threshold,
      benchmark_source: indicatorModal.benchmark_source,
      dynamic_range:
        indicatorModal.score_bands.length > 0
          ? indicatorModal.score_bands.map((b) => ({
              from: b.from,
              to: b.to,
              label: b.label,
              color: b.color,
              ...(b.description ? { description: b.description } : {}),
            }))
          : null,
      ...(valueSource === "formula" && {
        update_mode: indicatorModal.update_mode,
      }),
      // Always sent, so removing every tag on edit actually untags.
      breakdowns,
    };

    try {
      if (indicatorModal.mode === "add") {
        await createIndicator(indicatorModal.domainId, payload);
        toast.success(t("toasts.indicatorCreated"));
      } else {
        await apiUpdateIndicator(
          indicatorModal.domainId,
          indicatorModal.id!,
          payload,
        );
        toast.success(t("toasts.indicatorUpdated"));
      }
      setIndicatorModal((i) => ({ ...i, open: false }));
      await loadScorecard();
    } catch {
      toast.error(t("toasts.indicatorSaveFailed"));
    } finally {
      setSavingIndicator(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const confirmDelete = async () => {
    if (!deleteTarget || !scorecard) return;
    setDeletingItem(true);
    try {
      if (deleteTarget.type === "domain") {
        await apiDeleteDomain(scorecard.id, deleteTarget.id, { force: true });
      } else {
        await apiDeleteIndicator(deleteTarget.domainId!, deleteTarget.id);
      }
      setDeleteTarget(null);
      toast.success(t("toasts.deleteSuccess"));
      await loadScorecard();
    } catch {
      toast.error(t("toasts.deleteFailed"));
    } finally {
      setDeletingItem(false);
    }
  };

  // ── Publish ───────────────────────────────────────────────────────────────

  const handlePublish = async () => {
    if (!scorecardId) return;
    setPublishingScorecard(true);
    try {
      await apiPublishScorecard(scorecardId, {
        pin: pinToDashboard || undefined,
      });
      setPublishModalOpen(false);
      toast.success(t("toasts.scorecardPublished"));
      await loadScorecard();
      if (scorecard) loadHistory(scorecard.id);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "bsc_pending_approval") {
        toast.error(
          t("publishing.pendingApproval", { count: detail.pending_count }),
        );
      } else if (err?.response?.status === 409) {
        const msg =
          typeof detail === "string" ? detail : t("toasts.publishFailed");
        toast.error(msg);
      } else {
        toast.error(t("toasts.publishScorecardFailed"));
      }
    } finally {
      setPublishingScorecard(false);
    }
  };

  // ── History: open snapshot preview ────────────────────────────────────────

  const openVersionPreview = async (snapshotId: string) => {
    if (!scorecard) return;
    setPreviewingId(snapshotId);
    try {
      const full = await getHistorySnapshot(scorecard.id, snapshotId);
      setPreviewPub(full);
    } catch {
      toast.error(t("toasts.loadSnapshotFailed"));
    } finally {
      setPreviewingId(null);
    }
  };

  // ── History: export chart + table to PDF via print ─────────────────────────

  const handleExportHistory = () => {
    const styleId = "bsc-history-print-css";
    let style = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement("style");
      style.id = styleId;
      document.head.appendChild(style);
    }
    style.innerHTML = `
      @media print {
        body * { visibility: hidden !important; }
        #bsc-history-printable,
        #bsc-history-printable * { visibility: visible !important; }
        #bsc-history-printable {
          position: absolute !important;
          top: 0 !important; left: 0 !important;
          width: 100% !important;
          background: white !important;
          padding: 24px !important;
          box-sizing: border-box !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      }
    `;
    window.print();
    setTimeout(() => style?.remove(), 1000);
  };

  // ── Filters ───────────────────────────────────────────────────────────────

  const getFilteredDomains = (): ApiDomainWithIndicators[] => {
    if (!scorecard) return [];
    let domains = scorecard.domains;
    if (filterDomainId)
      domains = domains.filter((d) => d.id === filterDomainId);
    if (filterStatus)
      domains = domains.filter((d) => d.traffic_light === filterStatus);
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      domains = domains
        .map((d) => ({
          ...d,
          indicators: d.indicators.filter(
            (i) =>
              i.name.toLowerCase().includes(q) ||
              d.name.toLowerCase().includes(q),
          ),
        }))
        .filter(
          (d) => d.name.toLowerCase().includes(q) || d.indicators.length > 0,
        );
    }
    return domains;
  };

  const totalWeight =
    scorecard?.domains.reduce((s, d) => s + Number(d.weight), 0) ?? 0;

  // ── Render: Live diff banner ──────────────────────────────────────────────

  const renderLiveDiffBanner = () => {
    const diff = scorecard?.live_diff;
    if (!diff?.has_unpublished_changes) return null;
    return (
      <div className="bsc-live-diff-banner">
        <span className="bsc-live-diff-icon">⚡</span>
        <span className="bsc-live-diff-text">
          <strong>
            {diff.changed_indicator_count} indicator
            {diff.changed_indicator_count !== 1 ? "s" : ""} changed
          </strong>{" "}
          since last publish.
          {diff.overall_delta !== null && (
            <span
              className={`bsc-live-diff-delta ${diff.overall_delta > 0 ? "bsc-live-diff-delta--up" : "bsc-live-diff-delta--down"}`}
            >
              {" "}
              Overall score {diff.overall_delta > 0 ? "▲" : "▼"}{" "}
              {Math.abs(diff.overall_delta)} pts (
              {diff.published_overall_score ?? "—"} →{" "}
              {diff.live_overall_score ?? "—"})
            </span>
          )}
        </span>
        <button
          className="bsc-live-diff-publish-btn"
          onClick={() => setPublishModalOpen(true)}
        >
          {t("buttons.publishNow")}
        </button>
      </div>
    );
  };

  // ── Render: Indicators tab ────────────────────────────────────────────────

  const renderIndicatorsTab = () => {
    if (!scorecard) return null;
    const allIndicators: Array<
      ApiIndicator & { domainName: string; domainColor: string }
    > = scorecard.domains.flatMap((d) =>
      d.indicators.map((i) => ({
        ...i,
        domainName: d.name,
        domainColor: d.color,
      })),
    );

    // apply same filters as scorecard tab
    const filtered = allIndicators.filter((i) => {
      if (filterDomainId && i.domain_id !== filterDomainId) return false;
      if (filterStatus && i.traffic_light !== filterStatus) return false;
      if (
        searchText.trim() &&
        !i.name.toLowerCase().includes(searchText.toLowerCase())
      )
        return false;
      return true;
    });

    return (
      <div className="bsc-ind-flat">
        <div className="bsc-filter-bar">
          <Input
            placeholder={t("filters.searchIndicators")}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="bsc-filter-search"
            prefix={<span style={{ color: "#9ca3af", fontSize: 13 }}>🔍</span>}
          />
          <Select
            placeholder={t("filters.filterByDomain")}
            allowClear
            value={filterDomainId}
            onChange={setFilterDomainId}
            className="bsc-filter-select"
            options={scorecard.domains.map((d) => ({
              value: d.id,
              label: d.name,
            }))}
          />
          <Select
            placeholder={t("filters.filterByStatus")}
            allowClear
            value={filterStatus}
            onChange={setFilterStatus}
            className="bsc-filter-select"
            options={[
              { value: "green", label: t("status.onTrack") },
              { value: "yellow", label: t("status.atRisk") },
              { value: "red", label: t("status.critical") },
            ]}
          />
        </div>

        {filtered.length === 0 ? (
          <div className="bsc-no-results">{t("messages.noIndicators")}</div>
        ) : (
          <table className="bsc-flat-table">
            <thead>
              <tr>
                <th className="bsc-flat-th">{t("table.indicator")}</th>
                <th className="bsc-flat-th">{t("table.domain")}</th>
                <th className="bsc-flat-th">{t("table.source")}</th>
                <th className="bsc-flat-th">{t("table.update")}</th>
                <th className="bsc-flat-th">{t("table.currentValue")}</th>
                <th className="bsc-flat-th">{t("table.target")}</th>
                <th className="bsc-flat-th">{t("table.benchmarks")}</th>
                <th className="bsc-flat-th">{t("table.weight")}</th>
                <th className="bsc-flat-th">{t("table.score")}</th>
                <th className="bsc-flat-th">{t("table.status")}</th>
                <th className="bsc-flat-th">{t("table.approval")}</th>
                <th className="bsc-flat-th" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((ind) => {
                const tl = tlConf(ind.traffic_light, t);
                const approvalColor =
                  ind.approval_status === "accepted"
                    ? "#16a34a"
                    : ind.approval_status === "rejected"
                      ? "#dc2626"
                      : "#6b7280";
                const approvalBg =
                  ind.approval_status === "accepted"
                    ? "#dcfce7"
                    : ind.approval_status === "rejected"
                      ? "#fee2e2"
                      : "#f3f4f6";
                const menu: MenuProps = {
                  items: [
                    {
                      key: "edit",
                      label: t("buttons.edit"),
                      icon: <EditOutlined />,
                      onClick: () => openEditIndicator(ind),
                    },
                    {
                      key: "approve",
                      label: t("buttons.approve"),
                      icon: <CheckOutlined style={{ color: "#16a34a" }} />,
                      onClick: () =>
                        setApprovalModal({
                          open: true,
                          indicatorId: ind.id,
                          domainId: ind.domain_id,
                          indicatorName: ind.name,
                          status: "accepted",
                          note: "",
                        }),
                    },
                    {
                      key: "reject",
                      label: t("buttons.reject"),
                      icon: (
                        <ExclamationCircleOutlined
                          style={{ color: "#dc2626" }}
                        />
                      ),
                      onClick: () =>
                        setApprovalModal({
                          open: true,
                          indicatorId: ind.id,
                          domainId: ind.domain_id,
                          indicatorName: ind.name,
                          status: "rejected",
                          note: "",
                        }),
                    },
                    {
                      key: "delete",
                      label: t("buttons.delete"),
                      icon: <DeleteOutlined />,
                      danger: true,
                      onClick: () =>
                        setDeleteTarget({
                          type: "indicator",
                          id: ind.id,
                          domainId: ind.domain_id,
                          label: ind.name,
                        }),
                    },
                  ],
                };
                return (
                  <tr key={ind.id} className="bsc-flat-row">
                    <td className="bsc-flat-td bsc-flat-td--name">
                      {ind.name}
                    </td>
                    <td className="bsc-flat-td">
                      <span
                        className="bsc-flat-domain-tag"
                        style={{
                          borderLeftColor: ind.domainColor,
                        }}
                      >
                        {ind.domainName}
                      </span>
                    </td>
                    <td className="bsc-flat-td">
                      {ind.value_source === "formula" ? (
                        <span className="bsc-update-mode bsc-update-mode--live">
                          ⚡ {t("modes.formula")}
                        </span>
                      ) : (
                        <span className="bsc-update-mode bsc-update-mode--manual">
                          ✏ {t("modes.manual")}
                        </span>
                      )}
                    </td>
                    <td className="bsc-flat-td">
                      {ind.update_mode === "live" ? (
                        <span className="bsc-update-mode bsc-update-mode--live">
                          <SyncOutlined /> {t("modes.live")}
                        </span>
                      ) : ind.update_mode === "snapshot" ? (
                        <span className="bsc-update-mode bsc-update-mode--snapshot">
                          <CameraOutlined /> {t("modes.snapshot")}
                        </span>
                      ) : (
                        <span className="bsc-update-mode bsc-update-mode--manual">
                          —
                        </span>
                      )}
                    </td>
                    <td className="bsc-flat-td">
                      {ind.current_value !== null ? (
                        ind.current_value
                      ) : (
                        <span className="bsc-ind-empty">—</span>
                      )}
                    </td>
                    <td className="bsc-flat-td">
                      {ind.target_value !== null ? (
                        ind.target_value
                      ) : (
                        <span className="bsc-ind-empty">—</span>
                      )}
                    </td>
                    <td className="bsc-flat-td">
                      {ind.benchmark_lower !== null ||
                      ind.benchmark_upper !== null ? (
                        <span className="bsc-flat-bench">
                          {ind.benchmark_lower ?? "—"} –{" "}
                          {ind.benchmark_upper ?? "—"}
                        </span>
                      ) : (
                        <span className="bsc-ind-empty">—</span>
                      )}
                    </td>
                    <td className="bsc-flat-td">{ind.weight}</td>
                    <td className="bsc-flat-td">
                      {ind.normalised_score !== null ? (
                        <span
                          className="bsc-score-badge"
                          style={{
                            color: scoreColor(ind.normalised_score),
                            background: scoreBackground(ind.normalised_score),
                          }}
                        >
                          {ind.normalised_score}
                        </span>
                      ) : (
                        <span className="bsc-ind-empty">—</span>
                      )}
                    </td>
                    <td className="bsc-flat-td">
                      <span
                        className="bsc-score-badge"
                        style={{ color: tl.color, background: tl.bg }}
                      >
                        {tl.label}
                      </span>
                    </td>
                    <td className="bsc-flat-td">
                      <span
                        className="bsc-score-badge"
                        style={{ color: approvalColor, background: approvalBg }}
                      >
                        {ind.approval_status}
                      </span>
                    </td>
                    <td className="bsc-flat-td bsc-flat-td--action">
                      <ManagerOnlyTip show={!canWrite}><Dropdown menu={menu} trigger={["click"]} disabled={!canWrite}>
                        <button className="bsc-more-btn">
                          <MoreOutlined />
                        </button>
                      </Dropdown></ManagerOnlyTip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  // ── Render: Benchmarks tab ────────────────────────────────────────────────

  const renderBenchmarksTab = () => {
    if (!scorecard) return null;
    const allIndicators: Array<
      ApiIndicator & { domainName: string; domainColor: string }
    > = scorecard.domains.flatMap((d) =>
      d.indicators.map((i) => ({
        ...i,
        domainName: d.name,
        domainColor: d.color,
      })),
    );

    return (
      <div className="bsc-benchmarks">
        <div className="bsc-benchmarks-header">
          <h3 className="bsc-benchmarks-title">
            {t("sections.benchmarkConfiguration")}
          </h3>
          <p className="bsc-benchmarks-sub">
            {t("messages.benchmarkInlineEdit")}
          </p>
        </div>

        {scorecard.domains.map((domain) => (
          <div key={domain.id} className="bsc-bench-domain-section">
            <div
              className="bsc-bench-domain-title"
              style={{ borderLeftColor: domain.color }}
            >
              <span
                className="bsc-bench-domain-dot"
                style={{ background: domain.color }}
              />
              {domain.name}
              <span className="bsc-bench-domain-weight">
                Weight: {domain.weight}
              </span>
            </div>

            {domain.indicators.length === 0 ? (
              <div className="bsc-bench-empty">
                {t("messages.noIndicatorsInDomain")}
              </div>
            ) : (
              <table className="bsc-bench-table">
                <thead>
                  <tr>
                    <th className="bsc-bench-th bsc-bench-th--name">
                      {t("table.indicator")}
                    </th>
                    <th className="bsc-bench-th">{t("table.scale")}</th>
                    <th className="bsc-bench-th">{t("table.current")}</th>
                    <th className="bsc-bench-th">
                      {t("table.lowerThreshold")}
                    </th>
                    <th className="bsc-bench-th">
                      {t("table.upperThreshold")}
                    </th>
                    <th className="bsc-bench-th">{t("table.target")}</th>
                    <th className="bsc-bench-th">{t("table.score")}</th>
                    <th className="bsc-bench-th">{t("table.status")}</th>
                    <th className="bsc-bench-th" />
                  </tr>
                </thead>
                <tbody>
                  {domain.indicators.map((ind) => {
                    const edit = benchmarkEdits[ind.id];
                    const lower = edit ? edit.lower : ind.benchmark_lower;
                    const upper = edit ? edit.upper : ind.benchmark_upper;
                    const target = edit ? edit.target : ind.target_value;
                    const isDirty = !!edit;
                    const isSaving = edit?.saving;
                    const tl = tlConf(ind.traffic_light, t);

                    const initEdit = () => {
                      if (!benchmarkEdits[ind.id]) {
                        setBenchmarkEdits((prev) => ({
                          ...prev,
                          [ind.id]: {
                            lower: ind.benchmark_lower,
                            upper: ind.benchmark_upper,
                            target: ind.target_value,
                            saving: false,
                          },
                        }));
                      }
                    };

                    return (
                      <tr
                        key={ind.id}
                        className={`bsc-bench-row ${isDirty ? "bsc-bench-row--dirty" : ""}`}
                      >
                        <td className="bsc-bench-td bsc-bench-td--name">
                          <div className="bsc-bench-ind-name">{ind.name}</div>
                          {ind.benchmark_source === "library" && (
                            <div className="bsc-bench-source-tag">
                              📚 Library
                            </div>
                          )}
                        </td>
                        <td className="bsc-bench-td">
                          <span className="bsc-bench-scale">
                            {ind.value_scale}
                          </span>
                        </td>
                        <td className="bsc-bench-td">
                          {ind.current_value !== null ? (
                            <strong>{ind.current_value}</strong>
                          ) : (
                            <span className="bsc-ind-empty">—</span>
                          )}
                        </td>
                        <td className="bsc-bench-td">
                          <InputNumber
                            value={lower ?? undefined}
                            min={0}
                            onChange={(v) => {
                              initEdit();
                              setBenchmarkEdits((prev) => ({
                                ...prev,
                                [ind.id]: {
                                  ...prev[ind.id],
                                  lower: v,
                                  saving: false,
                                },
                              }));
                            }}
                            className="bsc-bench-input"
                            placeholder="—"
                            size="small"
                          />
                        </td>
                        <td className="bsc-bench-td">
                          <InputNumber
                            value={upper ?? undefined}
                            min={0}
                            onChange={(v) => {
                              initEdit();
                              setBenchmarkEdits((prev) => ({
                                ...prev,
                                [ind.id]: {
                                  ...prev[ind.id],
                                  upper: v,
                                  saving: false,
                                },
                              }));
                            }}
                            className="bsc-bench-input"
                            placeholder="—"
                            size="small"
                          />
                        </td>
                        <td className="bsc-bench-td">
                          <InputNumber
                            value={target ?? undefined}
                            min={0}
                            onChange={(v) => {
                              initEdit();
                              setBenchmarkEdits((prev) => ({
                                ...prev,
                                [ind.id]: {
                                  ...prev[ind.id],
                                  target: v,
                                  saving: false,
                                },
                              }));
                            }}
                            className="bsc-bench-input"
                            placeholder="—"
                            size="small"
                          />
                        </td>
                        <td className="bsc-bench-td">
                          {ind.normalised_score !== null ? (
                            <span
                              className="bsc-score-badge"
                              style={{
                                color: scoreColor(ind.normalised_score),
                                background: scoreBackground(
                                  ind.normalised_score,
                                ),
                              }}
                            >
                              {ind.normalised_score}
                            </span>
                          ) : (
                            <span className="bsc-ind-empty">—</span>
                          )}
                        </td>
                        <td className="bsc-bench-td">
                          <span
                            className="bsc-score-badge"
                            style={{ color: tl.color, background: tl.bg }}
                          >
                            {tl.label}
                          </span>
                        </td>
                        <td className="bsc-bench-td bsc-bench-td--action">
                          {isDirty && (
                            <div className="bsc-bench-save-row">
                              <button
                                className="bsc-bench-save-btn"
                                disabled={isSaving}
                                onClick={() => saveBenchmark(ind)}
                              >
                                {isSaving ? (
                                  <Spin size="small" />
                                ) : (
                                  t("buttons.save")
                                )}
                              </button>
                              <button
                                className="bsc-bench-cancel-btn"
                                onClick={() =>
                                  setBenchmarkEdits((prev) => {
                                    const next = { ...prev };
                                    delete next[ind.id];
                                    return next;
                                  })
                                }
                              >
                                ✕
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        ))}

        {allIndicators.length === 0 && (
          <div className="bsc-no-results">
            {t("messages.addIndicatorsFirst")}
          </div>
        )}
      </div>
    );
  };

  // ── Render: Performance Overview ─────────────────────────────────────────

  const renderPerformanceOverview = () => {
    if (!scorecard || scorecard.status === "draft") return null;
    const score = scorecard.overall_score ?? 0;
    const pct = Math.min(Math.max(score, 0), 100);

    return (
      <div className="bsc-overview">
        <div className="bsc-overview-stats">
          <div className="bsc-stat bsc-stat--green">
            <span className="bsc-stat-num">{scorecard.summary.green}</span>
            <span className="bsc-stat-label">{t("status.green")}</span>
          </div>
          <div className="bsc-stat bsc-stat--yellow">
            <span className="bsc-stat-num">{scorecard.summary.yellow}</span>
            <span className="bsc-stat-label">{t("status.yellow")}</span>
          </div>
          <div className="bsc-stat bsc-stat--red">
            <span className="bsc-stat-num">{scorecard.summary.red}</span>
            <span className="bsc-stat-label">{t("status.red")}</span>
          </div>
          <div className="bsc-stat bsc-stat--gray">
            <span className="bsc-stat-num">{scorecard.summary.total}</span>
            <span className="bsc-stat-label">{t("status.indicators")}</span>
          </div>
        </div>
        <div className="bsc-overview-body">
          <div className="bsc-overview-donut-col">
            <p className="bsc-overview-donut-label">
              {t("sections.overallPerformance")}
            </p>
            <div className="bsc-donut-wrap">
              <div
                className="bsc-donut"
                style={{
                  background: `conic-gradient(${scoreColor(score)} 0% ${pct}%, #f3f4f6 ${pct}% 100%)`,
                }}
              >
                <div className="bsc-donut-inner">
                  <span
                    className="bsc-donut-score"
                    style={{ color: scoreColor(score) }}
                  >
                    {score}
                  </span>
                </div>
              </div>
            </div>
            <p className="bsc-overview-donut-status">
              {t("messages.scorecardInfoLive")}{" "}
              <span
                style={{
                  color: tlConf(scorecard.overall_traffic_light, t).color,
                  fontWeight: 600,
                }}
              >
                {tlConf(scorecard.overall_traffic_light, t).label}
              </span>
            </p>
          </div>
          <div className="bsc-overview-domains-col">
            <p className="bsc-overview-domains-label">
              {t("sections.performanceByDomain")}
            </p>
            {scorecard.domains.map((d) => (
              <div key={d.id} className="bsc-domain-perf-row">
                <span className="bsc-domain-perf-name">{d.name}</span>
                <div className="bsc-domain-perf-bar-wrap">
                  <div
                    className="bsc-domain-perf-bar"
                    style={{
                      width: `${Math.min(d.domain_score ?? 0, 100)}%`,
                      background: d.color,
                    }}
                  />
                </div>
                <span
                  className="bsc-domain-perf-score"
                  style={{ color: scoreColor(d.domain_score) }}
                >
                  {d.domain_score ?? "—"}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ── Render: Indicator row ─────────────────────────────────────────────────

  const renderIndicatorRow = (ind: ApiIndicator) => {
    const isExpanded = expandedIndicators.has(ind.id);
    const menu: MenuProps = {
      items: [
        {
          key: "edit",
          label: t("buttons.edit"),
          icon: <EditOutlined />,
          onClick: () => openEditIndicator(ind),
        },
        {
          key: "approve",
          label: t("buttons.approve"),
          icon: <CheckOutlined style={{ color: "#16a34a" }} />,
          onClick: () =>
            setApprovalModal({
              open: true,
              indicatorId: ind.id,
              domainId: ind.domain_id,
              indicatorName: ind.name,
              status: "accepted",
              note: "",
            }),
        },
        {
          key: "reject",
          label: t("buttons.reject"),
          icon: <ExclamationCircleOutlined style={{ color: "#dc2626" }} />,
          onClick: () =>
            setApprovalModal({
              open: true,
              indicatorId: ind.id,
              domainId: ind.domain_id,
              indicatorName: ind.name,
              status: "rejected",
              note: "",
            }),
        },
        {
          key: "delete",
          label: t("buttons.delete"),
          icon: <DeleteOutlined />,
          danger: true,
          onClick: () =>
            setDeleteTarget({
              type: "indicator",
              id: ind.id,
              domainId: ind.domain_id,
              label: ind.name,
            }),
        },
      ],
    };

    const updateModeEl =
      ind.update_mode === "live" ? (
        <span className="bsc-update-mode bsc-update-mode--live">
          <SyncOutlined /> {t("modes.live")}
        </span>
      ) : ind.update_mode === "snapshot" ? (
        <span className="bsc-update-mode bsc-update-mode--snapshot">
          <CameraOutlined /> {t("modes.snapshot")}
        </span>
      ) : (
        <span className="bsc-update-mode bsc-update-mode--manual">—</span>
      );

    return (
      <React.Fragment key={ind.id}>
        <tr
          className={`bsc-ind-row ${isExpanded ? "bsc-ind-row--expanded" : ""}`}
          onClick={() =>
            setExpandedIndicators((prev) => {
              const n = new Set(prev);
              n.has(ind.id) ? n.delete(ind.id) : n.add(ind.id);
              return n;
            })
          }
        >
          <td className="bsc-ind-td bsc-ind-td--name">
            <div className="bsc-ind-name">{ind.name}</div>
            {ind.value_source === "formula" && ind.linked_formula_id ? (
              <div className="bsc-ind-source">
                {t("labels.source")}: <span>{t("messages.formulaLinked")}</span>
              </div>
            ) : (
              <div className="bsc-ind-source bsc-ind-source--manual">
                {t("messages.sourceManuallyEntered")}
              </div>
            )}
          </td>
          <td className="bsc-ind-td bsc-ind-td--mode">{updateModeEl}</td>
          <td className="bsc-ind-td bsc-ind-td--value">
            {ind.current_value !== null ? (
              <span>{ind.current_value}</span>
            ) : (
              <span className="bsc-ind-empty">—</span>
            )}
          </td>
          <td className="bsc-ind-td bsc-ind-td--weight">{ind.weight}</td>
          <td className="bsc-ind-td bsc-ind-td--score">
            {ind.normalised_score !== null ? (
              <span
                className="bsc-score-badge"
                style={{
                  color: scoreColor(ind.normalised_score),
                  background: scoreBackground(ind.normalised_score),
                }}
              >
                {ind.normalised_score}
              </span>
            ) : (
              <span className="bsc-ind-empty">—</span>
            )}
          </td>
          <td
            className="bsc-ind-td bsc-ind-td--action"
            onClick={(e) => e.stopPropagation()}
          >
            <ManagerOnlyTip show={!canWrite}><Dropdown menu={menu} trigger={["click"]} disabled={!canWrite}>
              <button className="bsc-more-btn">
                <MoreOutlined />
              </button>
            </Dropdown></ManagerOnlyTip>
          </td>
        </tr>
        {isExpanded && (
          <tr className="bsc-ind-detail-row">
            <td colSpan={6}>
              <div className="bsc-ind-detail">
                {(ind.benchmark_lower !== null ||
                  ind.benchmark_upper !== null) && (
                  <div className="bsc-ind-threshold-section">
                    <div className="bsc-ind-threshold-header">
                      <span className="bsc-ind-detail-label">
                        {t("labels.benchmarks")}: {ind.benchmark_lower ?? 0}% -{" "}
                        {ind.benchmark_upper ?? 100}%
                      </span>
                      {ind.target_value !== null && (
                        <span className="bsc-ind-detail-label bsc-ind-target-label">
                          {t("labels.target")}: {ind.target_value}
                        </span>
                      )}
                    </div>
                    <div className="bsc-threshold-track-wrap">
                      <div className="bsc-threshold-track-bg">
                        <div
                          className="bsc-threshold-range-fill"
                          style={{
                            left: `${ind.benchmark_lower ?? 0}%`,
                            width: `${(ind.benchmark_upper ?? 100) - (ind.benchmark_lower ?? 0)}%`,
                          }}
                        />
                        {ind.target_value !== null && (
                          <div
                            className="bsc-threshold-target-line"
                            style={{
                              left: `${Math.min(ind.target_value, 100)}%`,
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {ind.external_benchmark_label && (
                  <div className="bsc-ind-detail-block">
                    <span className="bsc-ind-detail-label">
                      {t("labels.benchmark")}
                    </span>
                    <span className="bsc-ind-benchmark">
                      <span className="bsc-ind-benchmark-icon">☐</span>{" "}
                      {t("labels.from")}{" "}
                      {ind.external_benchmark_url ? (
                        <a
                          href={ind.external_benchmark_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {ind.external_benchmark_label}
                        </a>
                      ) : (
                        <span className="bsc-ind-benchmark-link">
                          {ind.external_benchmark_label}
                        </span>
                      )}
                    </span>
                  </div>
                )}
                {ind.current_band && (
                  <div className="bsc-ind-detail-block">
                    <span className="bsc-ind-detail-label">
                      {t("labels.currentBand")}
                    </span>
                    <span className="bsc-ind-detail-value">
                      {ind.current_band}
                    </span>
                  </div>
                )}
                {ind.approval_status && ind.approval_status !== "pending" && (
                  <div className="bsc-ind-detail-block">
                    <span className="bsc-ind-detail-label">
                      {t("labels.approval")}
                    </span>
                    <span
                      className="bsc-score-badge"
                      style={{
                        color:
                          ind.approval_status === "accepted"
                            ? "#16a34a"
                            : "#dc2626",
                        background:
                          ind.approval_status === "accepted"
                            ? "#dcfce7"
                            : "#fee2e2",
                      }}
                    >
                      {ind.approval_status}
                    </span>
                  </div>
                )}
              </div>
            </td>
          </tr>
        )}
        {breakdownDimId && breakdownData && (
          <tr className="bsc-ind-bd-row">
            <td colSpan={6}>
              <IndicatorBreakdown
                dimensionName={breakdownData.dimension.name}
                categories={breakdownData.categories}
                indicator={breakdownData.domains
                  .flatMap((d) => d.indicators)
                  .find((i) => i.id === ind.id)}
              />
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  // ── Render: Domain ────────────────────────────────────────────────────────

  const renderDomain = (domain: ApiDomainWithIndicators) => {
    const isExpanded = expandedDomains.has(domain.id);
    const statusConf = tlConf(domain.traffic_light, t);
    const domainTotalWeight = domain.indicators.reduce(
      (s, i) => s + Number(i.weight),
      0,
    );
    const menu: MenuProps = {
      items: [
        {
          key: "edit",
          label: t("buttons.editDomain"),
          icon: <EditOutlined />,
          onClick: () => openEditDomain(domain),
        },
        {
          key: "delete",
          label: t("buttons.deleteDomain"),
          icon: <DeleteOutlined />,
          danger: true,
          onClick: () =>
            setDeleteTarget({
              type: "domain",
              id: domain.id,
              label: domain.name,
            }),
        },
      ],
    };

    return (
      <div
        key={domain.id}
        className="bsc-domain"
        style={{ borderLeftColor: domain.color }}
      >
        <div
          className="bsc-domain-header"
          onClick={() =>
            setExpandedDomains((prev) => {
              const n = new Set(prev);
              n.has(domain.id) ? n.delete(domain.id) : n.add(domain.id);
              return n;
            })
          }
        >
          <span className="bsc-domain-drag-handle">⠿⠿</span>
          <span className="bsc-domain-chevron">
            {isExpanded ? <DownOutlined /> : <RightOutlined />}
          </span>
          <span className="bsc-domain-name">{domain.name}</span>
          <span className="bsc-domain-weight">
            {t("labels.domainWeight")}: {domain.weight}
          </span>
          <span
            className="bsc-domain-score-badge"
            style={{ color: statusConf.color, background: statusConf.bg }}
          >
            {domain.domain_score !== null
              ? `${domain.domain_score} · ${statusConf.label}`
              : statusConf.label}
          </span>
          <div
            className="bsc-domain-header-actions"
            onClick={(e) => e.stopPropagation()}
          >
            <ManagerOnlyTip show={!canWrite}><Dropdown menu={menu} trigger={["click"]} disabled={!canWrite}>
              <button className="bsc-more-btn">
                <MoreOutlined />
              </button>
            </Dropdown></ManagerOnlyTip>
          </div>
        </div>

        {isExpanded && (
          <div className="bsc-domain-body">
            {domain.indicators.length > 0 ? (
              <table className="bsc-ind-table">
                <thead>
                  <tr>
                    <th className="bsc-ind-th bsc-ind-th--name">
                      {t("table.indicator")}
                    </th>
                    <th className="bsc-ind-th bsc-ind-th--mode">
                      {t("table.updateMode")}
                    </th>
                    <th className="bsc-ind-th bsc-ind-th--value">
                      {t("table.currentValue")}
                    </th>
                    <th className="bsc-ind-th bsc-ind-th--weight">
                      {t("table.weight")}
                    </th>
                    <th className="bsc-ind-th bsc-ind-th--score">
                      {t("table.normalisedScore")}
                    </th>
                    <th className="bsc-ind-th bsc-ind-th--action" />
                  </tr>
                </thead>
                <tbody>
                  {domain.indicators.map((ind) => renderIndicatorRow(ind))}
                </tbody>
              </table>
            ) : (
              <div className="bsc-domain-empty">
                {t("messages.noIndicatorInDomain")}
              </div>
            )}
            <div className="bsc-domain-footer">
              <span className="bsc-domain-total-weight">
                {t("labels.totalIndicatorWeight")}{" "}
                <strong
                  style={{
                    color: domainTotalWeight === 100 ? "#16a34a" : "#d97706",
                  }}
                >
                  {domainTotalWeight}
                </strong>
                /100
              </span>
              <ManagerOnlyTip show={!canWrite}>
                <button
                  className="bsc-add-ind-btn"
                  disabled={!canWrite}
                  onClick={(e) => {
                    e.stopPropagation();
                    openAddIndicator(domain.id);
                  }}
                >
                  <PlusOutlined /> {t("buttons.addIndicator")}
                </button>
              </ManagerOnlyTip>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Render: Scorecard tab ─────────────────────────────────────────────────

  const renderScorecardTab = () => {
    if (!scorecard) return null;
    const statusConf = {
      draft: { label: "Draft", color: "#6b7280", bg: "#f3f4f6" },
      published: { label: "Published", color: "#16a34a", bg: "#dcfce7" },
      outdated: { label: "Outdated", color: "#d97706", bg: "#fef3c7" },
    }[scorecard.status];
    const filteredDomains = getFilteredDomains();

    return (
      <div className="bsc-scorecard-content">
        <div className="bsc-sc-header">
          <div className="bsc-sc-title-row">
            {editingTitle ? (
              <div className="bsc-title-edit">
                <Input
                  value={titleInput}
                  onChange={(e) => setTitleInput(e.target.value)}
                  onPressEnter={handleSaveTitle}
                  autoFocus
                  style={{ fontSize: 18, fontWeight: 700 }}
                />
                <button
                  className="bsc-title-save-btn"
                  onClick={handleSaveTitle}
                  disabled={savingTitle}
                >
                  {savingTitle ? <Spin size="small" /> : <CheckOutlined />} {t("buttons.save")}
                </button>
                <button
                  className="bsc-btn-cancel"
                  onClick={() => {
                    setEditingTitle(false);
                    setTitleInput(scorecard.title);
                  }}
                >
                  {t("buttons.cancel")}
                </button>
              </div>
            ) : (
              <div className="bsc-title-display">
                <h2 className="bsc-sc-title">{scorecard.title}</h2>
                <button
                  className="bsc-edit-title-btn"
                  onClick={() => setEditingTitle(true)}
                >
                  <EditOutlined /> {t("buttons.edit")}
                </button>
                <span
                  className="bsc-status-chip"
                  style={{ color: statusConf.color, background: statusConf.bg }}
                >
                  {statusConf.label}
                </span>
              </div>
            )}
            <div className="bsc-sc-actions">
              <ManagerOnlyTip show={!canWrite}>
                <button
                  className="bsc-publish-btn"
                  disabled={!canWrite}
                  onClick={() => setPublishModalOpen(true)}
                >
                  {t("buttons.publishScorecard")}
                </button>
              </ManagerOnlyTip>
              <button
                type="button"
                className="bsc-settings-btn"
                aria-label={t("alerts.settingsAria")}
                onClick={() => setSettingsOpen(true)}
              >
                <SettingOutlined />
              </button>
              <button
                className="bsc-save-btn"
                onClick={() =>
                  toast.info(t("toasts.changesSavedAutomatically"))
                }
              >
                {t("buttons.saveChanges")}
              </button>
            </div>
          </div>
          {scorecard.published_at && (
            <p className="bsc-last-published">
              {t("messages.lastPublished", {
                date: dayjs(scorecard.published_at).format("MMM D, YYYY"),
              })}
            </p>
          )}
        </div>

        {renderLiveDiffBanner()}

        {(scorecard.status === "published" ||
          scorecard.status === "outdated") &&
          renderPerformanceOverview()}

        <div className="bsc-domains-section">
          <div className="bsc-domains-section-header">
            <h3 className="bsc-domains-title">
              {t("sections.domainsAndIndicators")}
            </h3>
            <p className="bsc-domains-subtitle">
              Organize indicators into domains and define how much each domain
              contributes to the overall Score.
            </p>
          </div>
          <div className="bsc-filter-bar">
            <Input
              placeholder={t("filters.search")}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="bsc-filter-search"
              suffix={<SearchOutlined style={{ color: "#9ca3af" }} />}
            />
            <Select
              placeholder={t("filters.filterByDomain")}
              allowClear
              value={filterDomainId}
              onChange={setFilterDomainId}
              className="bsc-filter-select"
              options={scorecard.domains.map((d) => ({
                value: d.id,
                label: d.name,
              }))}
            />
            <Select
              placeholder={t("filters.filterByStatus")}
              allowClear
              value={filterStatus}
              onChange={setFilterStatus}
              className="bsc-filter-select"
              options={[
                { value: "green", label: t("status.onTrack") },
                { value: "yellow", label: t("status.atRisk") },
                { value: "red", label: t("status.critical") },
              ]}
            />
            <Select
              placeholder={t("filters.sortBy")}
              allowClear
              className="bsc-filter-select"
              options={[
                { value: "weight_desc", label: t("sortOptions.weightDesc") },
                { value: "weight_asc", label: t("sortOptions.weightAsc") },
                { value: "score_desc", label: t("sortOptions.scoreDesc") },
                { value: "score_asc", label: t("sortOptions.scoreAsc") },
              ]}
            />
            <button
              className="bsc-dim-toolbar-btn"
              onClick={() => setBulkOpen(true)}
              // Writes need Owner/Manager; viewers can see the grid but not save.
              disabled={
                dimensions.length === 0 || !canManageUsers(getCurrentRole())
              }
              title={
                dimensions.length === 0
                  ? t("bulkEntry.addDimensionFirst")
                  : undefined
              }
            >
              <TableOutlined /> {t("bulkEntry.title")}
            </button>
            <ManagerOnlyTip show={!canWrite}>
              <button
                className="bsc-dim-toolbar-btn"
                onClick={() => setManageDimOpen(true)}
                disabled={!canWrite}
              >
                {t("dimensions.toolbar")}
                {dimensions.length > 0 && (
                  <span className="bsc-dim-badge">{dimensions.length}</span>
                )}
              </button>
            </ManagerOnlyTip>
            <ManagerOnlyTip show={!canWrite}>
              <button
                className="bsc-add-domain-btn"
                disabled={!canWrite}
                onClick={openAddDomain}
              >
                <PlusOutlined /> {t("buttons.addDomain")}
              </button>
            </ManagerOnlyTip>
          </div>
          <div className="bsc-overall-weight bsc-overall-row">
            <div>
              {t("labels.overallWeight")}{" "}
              <strong
                style={{ color: totalWeight === 100 ? "#16a34a" : "#d97706" }}
              >
                {totalWeight}
              </strong>{" "}
              / 100
              {scorecard.domains.some((d) => d.indicators.length === 0) && (
                <span className="bsc-weight-warning">
                  ⚠ {t("messages.emptyDomainsBlockPublish")}
                </span>
              )}
            </div>
            <div className="bsc-breakdown-select">
              <span className="bsc-breakdown-label">{t("breakdown.label")}</span>
              <Select
                value={breakdownDimId ?? "none"}
                onChange={(v: string) =>
                  setBreakdownDimId(v === "none" ? null : v)
                }
                style={{ minWidth: 160 }}
                options={[
                  { value: "none", label: t("breakdown.none") },
                  ...dimensions.map((d) => ({ value: d.id, label: d.name })),
                ]}
              />
            </div>
          </div>
          <div className="bsc-domains-list">
            {scorecard.domains.length === 0 ? (
              <div className="bsc-domains-empty">
                <div className="bsc-domains-empty-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                    <rect
                      x="3"
                      y="3"
                      width="8"
                      height="8"
                      rx="1.5"
                      stroke="#d1d5db"
                      strokeWidth="1.5"
                    />
                    <rect
                      x="13"
                      y="3"
                      width="8"
                      height="8"
                      rx="1.5"
                      stroke="#d1d5db"
                      strokeWidth="1.5"
                    />
                    <rect
                      x="3"
                      y="13"
                      width="8"
                      height="8"
                      rx="1.5"
                      stroke="#d1d5db"
                      strokeWidth="1.5"
                    />
                    <rect
                      x="13"
                      y="13"
                      width="8"
                      height="8"
                      rx="1.5"
                      stroke="#d1d5db"
                      strokeWidth="1.5"
                    />
                  </svg>
                </div>
                <h4 className="bsc-domains-empty-title">
                  {t("messages.startWithYourDomains")}
                </h4>
                <p className="bsc-domains-empty-sub">
                  {t("messages.addFirstDomainDescription")}
                </p>
                <ManagerOnlyTip show={!canWrite}>
                  <button
                    className="bsc-add-first-domain-btn"
                    disabled={!canWrite}
                    onClick={openAddDomain}
                  >
                    <PlusOutlined /> {t("buttons.addFirstDomain")}
                  </button>
                </ManagerOnlyTip>
                <p className="bsc-domains-empty-or">
                  {t("common.or")}{" "}
                  <button
                    className="bsc-link-btn"
                    onClick={() => setTemplateModalOpen(true)}
                  >
                    {t("buttons.browseTemplates")}
                  </button>
                </p>
              </div>
            ) : filteredDomains.length === 0 ? (
              <div className="bsc-no-results">
                {t("messages.noResultsMatchFilters")}
              </div>
            ) : (
              filteredDomains.map((d) => renderDomain(d))
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Render: History tab ───────────────────────────────────────────────────

  const renderHistoryTab = () => {
    if (histLoading) {
      return (
        <div className="bsc-history">
          <div style={{ padding: 40, textAlign: "center" }}>
            <Spin />
          </div>
        </div>
      );
    }
    if (!history || history.snapshots.length === 0) {
      return (
        <div className="bsc-history">
          <div className="bsc-history-tab-header">
            <div>
              <h3 className="bsc-history-title">{t("tabs.history")}</h3>
              <p className="bsc-history-desc">
                {t("messages.historyDescription")}
              </p>
            </div>
          </div>
          <div className="bsc-history-empty">
            {t("messages.noPublishedSnapshots")}
          </div>
        </div>
      );
    }

    const trend = history.trend.filter(
      (p) => !hiddenVersions.has(p.version_number),
    );
    const snapshots = history.snapshots;

    return (
      <div className="bsc-history">
        {/* Header */}
        <div className="bsc-history-tab-header">
          <div>
            <h3 className="bsc-history-title">{t("tabs.history")}</h3>
            <p className="bsc-history-desc">
              {t("messages.historyDescription")}
            </p>
          </div>
          <div className="bsc-history-actions">
            <label className="bsc-pin-label">
              <Checkbox
                checked={pinToDashboard}
                onChange={(e) => togglePinToDashboard(e.target.checked)}
              />
              <span>{t("buttons.pinToDashboard")}</span>
            </label>
            {canManageUsers(getCurrentRole()) && (
              <button
                className="bsc-share-btn"
                onClick={() => setShareModalOpen(true)}
              >
                {t("buttons.share")}
              </button>
            )}
            <button className="bsc-export-btn" onClick={handleExportHistory}>
              + {t("buttons.export")}
            </button>
          </div>
        </div>

        <div id="bsc-history-printable">
          {/* Score over time chart */}
          <div className="bsc-hist-chart-card">
            <div className="bsc-hist-chart-header">
              <span className="bsc-hist-chart-title">
                {t("history.scoreOverTime")}
              </span>
              <div className="bsc-hist-chart-legend">
                <span
                  className="bsc-hist-legend-item"
                  style={{ color: "#5540af" }}
                >
                  — {t("history.overall")}
                </span>
                {trend[0]?.domains.map((d, i) => (
                  <span
                    key={i}
                    className="bsc-hist-legend-item"
                    style={{ color: d.color }}
                  >
                    — {d.name}
                  </span>
                ))}
              </div>
            </div>
            <div className="bsc-hist-chart-body">
              <div className="bsc-hist-chart-yaxis">
                {[100, 75, 50, 25, 0].map((v) => (
                  <span key={v} className="bsc-hist-yaxis-label">
                    {v}
                  </span>
                ))}
              </div>
              <div className="bsc-hist-chart-area">
                <svg
                  width="100%"
                  height="120"
                  viewBox="0 0 600 120"
                  preserveAspectRatio="none"
                  style={{ overflow: "visible" }}
                >
                  {[0, 30, 60, 90, 120].map((y) => (
                    <line
                      key={y}
                      x1="0"
                      y1={y}
                      x2="600"
                      y2={y}
                      stroke="#f3f4f6"
                      strokeWidth="1"
                    />
                  ))}
                  {trend.length > 1 &&
                    (() => {
                      const pts = trend
                        .map((p, i) => {
                          const x = (i / (trend.length - 1)) * 600;
                          const y = 120 - ((p.overall_score ?? 0) / 100) * 120;
                          return `${x.toFixed(0)},${y.toFixed(0)}`;
                        })
                        .join(" ");
                      return (
                        <polyline
                          key="overall"
                          points={pts}
                          fill="none"
                          stroke="#4a1c96"
                          strokeWidth="2.5"
                          strokeLinejoin="round"
                        />
                      );
                    })()}
                  {trend[0]?.domains.map((_, di) => {
                    if (trend.length < 2) return null;
                    const pts = trend
                      .map((p, i) => {
                        const x = (i / (trend.length - 1)) * 600;
                        const domScore = p.domains[di]?.domain_score ?? 0;
                        const y = 120 - (domScore / 100) * 120;
                        return `${x.toFixed(0)},${y.toFixed(0)}`;
                      })
                      .join(" ");
                    return (
                      <polyline
                        key={di}
                        points={pts}
                        fill="none"
                        stroke={trend[0].domains[di]?.color ?? "#ccc"}
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                        strokeDasharray="4 2"
                      />
                    );
                  })}
                  {trend.map((p, i) => {
                    const x =
                      trend.length > 1 ? (i / (trend.length - 1)) * 600 : 300;
                    const y = 120 - ((p.overall_score ?? 0) / 100) * 120;
                    return (
                      <circle key={i} cx={x} cy={y} r="4" fill="#4a1c96" />
                    );
                  })}
                </svg>
                <div className="bsc-hist-xaxis">
                  {trend.map((p, i) => (
                    <span key={i} className="bsc-hist-xaxis-label">
                      <span className="bsc-hist-xaxis-version">
                        {p.version_label}
                      </span>
                      {dayjs(p.published_at).format("MMM D")}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Publications table */}
          <div className="bsc-hist-table-wrap">
            <table className="bsc-hist-table">
              <thead>
                <tr>
                  <th className="bsc-hist-th bsc-hist-th--check">
                    <Checkbox
                      checked={hiddenVersions.size === 0}
                      indeterminate={
                        hiddenVersions.size > 0 &&
                        hiddenVersions.size < snapshots.length
                      }
                      onChange={(e) =>
                        setHiddenVersions(
                          e.target.checked
                            ? new Set()
                            : new Set(snapshots.map((s) => s.version_number)),
                        )
                      }
                    />
                  </th>
                  <th className="bsc-hist-th">{t("historyView.version")}</th>
                  <th className="bsc-hist-th">{t("table.published")}</th>
                  <th className="bsc-hist-th">{t("table.score")}</th>
                  <th className="bsc-hist-th" />
                </tr>
              </thead>
              <tbody>
                {snapshots.map((snap) => {
                  const statusConf = tlConf(snap.overall_traffic_light, t);
                  const isVisible = !hiddenVersions.has(snap.version_number);
                  return (
                    <tr key={snap.id} className="bsc-hist-row">
                      <td className="bsc-hist-td">
                        <Checkbox
                          checked={isVisible}
                          onChange={(e) =>
                            setHiddenVersions((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) {
                                next.delete(snap.version_number);
                              } else {
                                next.add(snap.version_number);
                              }
                              return next;
                            })
                          }
                        />
                      </td>
                      <td className="bsc-hist-td">
                        <span className="bsc-hist-vlabel">
                          {snap.version_label}
                        </span>
                      </td>
                      <td className="bsc-hist-td">
                        <div className="bsc-hist-date">
                          {dayjs(snap.published_at).format("MMMM D, YYYY")}
                        </div>
                        <div className="bsc-hist-by">
                          {t("historyView.byName", {
                            name: snap.published_by.name,
                          })}
                        </div>
                      </td>
                      <td className="bsc-hist-td">
                        <span
                          className="bsc-score-badge"
                          style={{
                            color: statusConf.color,
                            background: statusConf.bg,
                          }}
                        >
                          {snap.overall_score ?? "—"} · {statusConf.label}
                        </span>
                      </td>
                      <td className="bsc-hist-td bsc-hist-td--action">
                        <button
                          className="bsc-hist-eye-btn"
                          aria-label={t("historyView.viewVersion")}
                          onClick={() => openVersionPreview(snap.id)}
                        >
                          {previewingId === snap.id ? (
                            <Spin size="small" />
                          ) : (
                            <EyeOutlined />
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Compare versions */}
          <div className="bsc-compare-section">
            <h4 className="bsc-compare-title">
              {t("history.compareVersions")}
            </h4>
            <p className="bsc-compare-sub">
              {t("messages.compareVersionsDesc")}
            </p>
            <div className="bsc-compare-selectors">
              <Select
                placeholder={t("filters.selectVersionA")}
                value={compareLeft}
                onChange={setCompareLeft}
                style={{ width: 200 }}
                options={snapshots.map((s) => ({
                  value: s.id,
                  label: `${s.version_label} (${dayjs(s.published_at).format("MMM D, YYYY")})`,
                }))}
              />
              <span className="bsc-compare-vs">{t("common.vs")}</span>
              <Select
                placeholder={t("filters.selectVersionB")}
                value={compareRight}
                onChange={setCompareRight}
                style={{ width: 200 }}
                options={snapshots.map((s) => ({
                  value: s.id,
                  label: `${s.version_label} (${dayjs(s.published_at).format("MMM D, YYYY")})`,
                }))}
              />
            </div>

            {compareResult && (
              <div className="bsc-compare-body">
                <div className="bsc-compare-row bsc-compare-row--header">
                  <div className="bsc-compare-label">
                    {t("publishSnapshot.overallScore")}
                  </div>
                  <div className="bsc-compare-col">
                    <span className="bsc-compare-version-tag">
                      {snapshots.find((s) => s.id === compareLeft)
                        ?.version_label ?? "—"}{" "}
                      ·{" "}
                      {dayjs(
                        snapshots.find((s) => s.id === compareLeft)
                          ?.published_at,
                      ).format("MMM D, YYYY")}
                    </span>
                    <span
                      className="bsc-compare-score-big"
                      style={{
                        color: scoreColor(compareResult.v1.overall_score),
                      }}
                    >
                      {compareResult.v1.overall_score ?? "—"}
                    </span>
                  </div>
                  <div className="bsc-compare-col">
                    <span className="bsc-compare-version-tag">
                      {snapshots.find((s) => s.id === compareRight)
                        ?.version_label ?? "—"}{" "}
                      ·{" "}
                      {dayjs(
                        snapshots.find((s) => s.id === compareRight)
                          ?.published_at,
                      ).format("MMM D, YYYY")}
                    </span>
                    <span
                      className="bsc-compare-score-big"
                      style={{
                        color: scoreColor(compareResult.v2.overall_score),
                      }}
                    >
                      {compareResult.v2.overall_score ?? "—"}
                    </span>
                  </div>
                  <div className="bsc-compare-col">
                    {compareResult.overall_delta !== null && (
                      <span
                        className={`bsc-compare-delta ${compareResult.overall_delta > 0 ? "bsc-compare-delta--up" : compareResult.overall_delta < 0 ? "bsc-compare-delta--down" : ""}`}
                      >
                        {compareResult.overall_delta > 0
                          ? "▲"
                          : compareResult.overall_delta < 0
                            ? "▼"
                            : "="}{" "}
                        {Math.abs(compareResult.overall_delta)}
                      </span>
                    )}
                  </div>
                </div>
                {compareResult.domains.map((d) => {
                  const isOpen = openCompareDomains.has(d.name);
                  const leftLabel =
                    snapshots.find((s) => s.id === compareLeft)?.version_label ??
                    "V1";
                  const rightLabel =
                    snapshots.find((s) => s.id === compareRight)?.version_label ??
                    "V2";
                  const deltaClass = (delta: number | null) =>
                    delta === null
                      ? ""
                      : delta > 0
                        ? "bsc-compare-delta--up"
                        : delta < 0
                          ? "bsc-compare-delta--down"
                          : "";
                  const deltaArrow = (delta: number) =>
                    delta > 0 ? "▲" : delta < 0 ? "▼" : "=";
                  return (
                    <div key={d.name} className="bsc-cmp-domain">
                      <button
                        type="button"
                        className="bsc-cmp-domain-head"
                        onClick={() =>
                          setOpenCompareDomains((prev) => {
                            const next = new Set(prev);
                            if (next.has(d.name)) next.delete(d.name);
                            else next.add(d.name);
                            return next;
                          })
                        }
                      >
                        <span
                          className="bsc-compare-dom-dot"
                          style={{
                            background:
                              scorecard?.domains.find(
                                (dom) => dom.name === d.name,
                              )?.color ?? "#6b7280",
                          }}
                        />
                        <span className="bsc-cmp-domain-name">{d.name}</span>
                        <span className="bsc-cmp-domain-score">
                          {t("historyView.scoreRange", {
                            from: d.score_v1 ?? "—",
                            to: d.score_v2 ?? "—",
                          })}
                          {d.delta !== null && (
                            <span
                              className={`bsc-compare-delta ${deltaClass(d.delta)}`}
                            >
                              {" "}
                              {deltaArrow(d.delta)} {Math.abs(d.delta)}
                            </span>
                          )}
                        </span>
                        {isOpen ? <DownOutlined /> : <RightOutlined />}
                      </button>
                      {isOpen && (
                        <table className="bsc-cmp-ind-table">
                          <thead>
                            <tr>
                              <th>{t("historyView.indicator")}</th>
                              <th>
                                {t("historyView.valueOf", { label: leftLabel })}
                              </th>
                              <th>
                                {t("historyView.valueOf", { label: rightLabel })}
                              </th>
                              <th>{t("historyView.delta")}</th>
                              <th>{t("historyView.lightChange")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {d.indicators.map((ind) => (
                              <tr key={ind.name}>
                                <td>{ind.name}</td>
                                <td>{ind.value_v1 ?? "—"}</td>
                                <td>{ind.value_v2 ?? "—"}</td>
                                <td>
                                  {ind.delta !== null ? (
                                    <span
                                      className={`bsc-compare-delta ${deltaClass(ind.delta)}`}
                                    >
                                      {deltaArrow(ind.delta)}{" "}
                                      {Math.abs(ind.delta)}
                                    </span>
                                  ) : (
                                    "—"
                                  )}
                                </td>
                                <td>
                                  <span className="bsc-cmp-light">
                                    <span
                                      className="bsc-cmp-dot"
                                      style={{
                                        background: tlConf(
                                          ind.traffic_light_v1,
                                          t,
                                        ).color,
                                      }}
                                    />
                                    <span className="bsc-cmp-arrow">
                                      {ind.traffic_light_v1 ===
                                      ind.traffic_light_v2
                                        ? "="
                                        : "→"}
                                    </span>
                                    <span
                                      className="bsc-cmp-dot"
                                      style={{
                                        background: tlConf(
                                          ind.traffic_light_v2,
                                          t,
                                        ).color,
                                      }}
                                    />
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {renderVersionPreview()}
      </div>
    );
  };

  // ── Render: Version preview modal ─────────────────────────────────────────

  const renderVersionPreview = () => {
    if (!previewPub) return null;
    const score = previewPub.overall_score ?? 0;
    const pct = Math.min(score, 100);
    // The API wraps the domains: snapshot_data = { title, domains, … }.
    const snapshotRaw: unknown = previewPub.snapshot_data;
    const snapshotData: ApiDomainWithIndicators[] = Array.isArray(snapshotRaw)
      ? snapshotRaw
      : Array.isArray((snapshotRaw as { domains?: unknown })?.domains)
        ? ((snapshotRaw as { domains: ApiDomainWithIndicators[] }).domains)
        : [];
    const allInds = snapshotData.flatMap((d) => d.indicators);
    const greenCnt = allInds.filter((i) => i.traffic_light === "green").length;
    const yellowCnt = allInds.filter(
      (i) => i.traffic_light === "yellow",
    ).length;
    const redCnt = allInds.filter((i) => i.traffic_light === "red").length;

    return (
      <Modal
        open={!!previewPub}
        onCancel={() => setPreviewPub(null)}
        footer={null}
        width={680}
        title={null}
        destroyOnClose
        style={{ top: 20 }}
        className="bsc-preview-modal-wrap"
      >
        <div className="bsc-vpreview">
          <div className="bsc-vpreview-header">
            <div>
              <h3 className="bsc-vpreview-title">
                {scorecard?.title ?? "Scorecard"} — Version{" "}
                {previewPub.version_number}
              </h3>
              <p className="bsc-vpreview-meta">
                Published{" "}
                {dayjs(previewPub.published_at).format("MMMM D, YYYY")} · by{" "}
                {previewPub.published_by.name}
              </p>
            </div>
          </div>

          <div className="bsc-overview-stats" style={{ marginBottom: 16 }}>
            <div className="bsc-stat bsc-stat--green">
              <span className="bsc-stat-num">{greenCnt}</span>
              <span className="bsc-stat-label">{t("status.green")}</span>
            </div>
            <div className="bsc-stat bsc-stat--yellow">
              <span className="bsc-stat-num">{yellowCnt}</span>
              <span className="bsc-stat-label">{t("status.yellow")}</span>
            </div>
            <div className="bsc-stat bsc-stat--red">
              <span className="bsc-stat-num">{redCnt}</span>
              <span className="bsc-stat-label">{t("status.red")}</span>
            </div>
            <div className="bsc-stat bsc-stat--gray">
              <span className="bsc-stat-num">{allInds.length}</span>
              <span className="bsc-stat-label">{t("status.indicators")}</span>
            </div>
          </div>

          <div className="bsc-overview-body" style={{ marginBottom: 20 }}>
            <div className="bsc-overview-donut-col">
              <p className="bsc-overview-donut-label">
                {t("sections.overallPerformance")}
              </p>
              <div className="bsc-donut-wrap">
                <div
                  className="bsc-donut"
                  style={{
                    background: `conic-gradient(${scoreColor(score)} 0% ${pct}%, #f3f4f6 ${pct}% 100%)`,
                  }}
                >
                  <div className="bsc-donut-inner">
                    <span
                      className="bsc-donut-score"
                      style={{ color: scoreColor(score) }}
                    >
                      {score}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="bsc-overview-domains-col">
              <p className="bsc-overview-domains-label">
                {t("sections.performanceByDomain")}
              </p>
              {snapshotData.map((d) => (
                <div key={d.id} className="bsc-domain-perf-row">
                  <span className="bsc-domain-perf-name">{d.name}</span>
                  <div className="bsc-domain-perf-bar-wrap">
                    <div
                      className="bsc-domain-perf-bar"
                      style={{
                        width: `${Math.min(d.domain_score ?? 0, 100)}%`,
                        background: d.color,
                      }}
                    />
                  </div>
                  <span
                    className="bsc-domain-perf-score"
                    style={{ color: scoreColor(d.domain_score) }}
                  >
                    {d.domain_score ?? "—"}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bsc-vpreview-domains">
            <div className="bsc-overall-weight" style={{ marginBottom: 12 }}>
              {t("labels.overallWeight")}: <strong>100</strong> / 100
            </div>
            {snapshotData.map((domain) => {
              const sc = tlConf(domain.traffic_light, t);
              return (
                <div
                  key={domain.id}
                  className="bsc-domain"
                  style={{ borderLeftColor: domain.color, marginBottom: 8 }}
                >
                  <div
                    className="bsc-domain-header"
                    style={{ cursor: "default" }}
                  >
                    <span className="bsc-domain-name">{domain.name}</span>
                    <span className="bsc-domain-weight">
                      {t("labels.domainWeight")}: {domain.weight}
                    </span>
                    <span
                      className="bsc-domain-score-badge"
                      style={{ color: sc.color, background: sc.bg }}
                    >
                      {domain.domain_score !== null
                        ? `${domain.domain_score} · ${sc.label}`
                        : sc.label}
                    </span>
                  </div>
                  <div className="bsc-domain-body">
                    {domain.indicators && domain.indicators.length > 0 ? (
                      <table className="bsc-ind-table">
                        <thead>
                          <tr>
                            <th className="bsc-ind-th bsc-ind-th--name">
                              {t("table.indicator")}
                            </th>
                            <th className="bsc-ind-th bsc-ind-th--mode">
                              {t("table.updateMode")}
                            </th>
                            <th className="bsc-ind-th bsc-ind-th--value">
                              {t("table.currentValue")}
                            </th>
                            <th className="bsc-ind-th bsc-ind-th--weight">
                              {t("table.weight")}
                            </th>
                            <th className="bsc-ind-th bsc-ind-th--score">
                              {t("table.normalisedScore")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {domain.indicators.map((ind) => {
                            const updateModeEl =
                              ind.update_mode === "live" ? (
                                <span className="bsc-update-mode bsc-update-mode--live">
                                  <SyncOutlined /> {t("modes.live")}
                                </span>
                              ) : ind.update_mode === "snapshot" ? (
                                <span className="bsc-update-mode bsc-update-mode--snapshot">
                                  <CameraOutlined /> {t("modes.snapshot")}
                                </span>
                              ) : (
                                <span className="bsc-update-mode bsc-update-mode--manual">
                                  —
                                </span>
                              );
                            return (
                              <tr
                                key={ind.id}
                                className="bsc-ind-row"
                                style={{ cursor: "default" }}
                              >
                                <td className="bsc-ind-td bsc-ind-td--name">
                                  <div className="bsc-ind-name">{ind.name}</div>
                                  <div className="bsc-ind-source bsc-ind-source--manual">
                                    {ind.value_source === "formula"
                                      ? t("modes.formulaLinked")
                                      : t("modes.manuallyEntered")}
                                  </div>
                                </td>
                                <td className="bsc-ind-td bsc-ind-td--mode">
                                  {updateModeEl}
                                </td>
                                <td className="bsc-ind-td bsc-ind-td--value">
                                  {ind.current_value ?? "—"}
                                </td>
                                <td className="bsc-ind-td bsc-ind-td--weight">
                                  {ind.weight}
                                </td>
                                <td className="bsc-ind-td bsc-ind-td--score">
                                  {ind.normalised_score !== null ? (
                                    <span
                                      className="bsc-score-badge"
                                      style={{
                                        color: scoreColor(ind.normalised_score),
                                        background: scoreBackground(
                                          ind.normalised_score,
                                        ),
                                      }}
                                    >
                                      {ind.normalised_score}
                                    </span>
                                  ) : (
                                    <span className="bsc-ind-empty">—</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : (
                      <div className="bsc-domain-empty">
                        {t("messages.noIndicatorsInSnapshot")}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Modal>
    );
  };

  // ── Render: Empty state ───────────────────────────────────────────────────

  const renderEmpty = () => (
    <div className="bsc-empty-state">
      <div className="bsc-empty-illustration">
        <svg width="120" height="100" viewBox="0 0 120 100" fill="none">
          <rect x="10" y="55" width="16" height="35" rx="3" fill="#e5e7eb" />
          <rect x="32" y="40" width="16" height="50" rx="3" fill="#d1d5db" />
          <rect x="54" y="25" width="16" height="65" rx="3" fill="#c7d2fe" />
          <rect x="76" y="15" width="16" height="75" rx="3" fill="#a5b4fc" />
          <rect x="98" y="30" width="16" height="60" rx="3" fill="#818cf8" />
          <line
            x1="5"
            y1="92"
            x2="115"
            y2="92"
            stroke="#e5e7eb"
            strokeWidth="2"
          />
        </svg>
      </div>
      <h3 className="bsc-empty-title">{t("messages.buildFirstScorecard")}</h3>
      <p className="bsc-empty-desc">{t("messages.buildFirstScorecardDesc")}</p>
      <div className="bsc-empty-actions">
        <button
          className="bsc-browse-btn"
          onClick={() => setTemplateModalOpen(true)}
        >
          {t("buttons.browseTemplates")}
        </button>
        <button className="bsc-scratch-btn" onClick={handleStartFromScratch}>
          {t("buttons.startFromScratch")}
        </button>
      </div>
    </div>
  );

  // ── Modals ────────────────────────────────────────────────────────────────

  const renderTemplateModal = () => (
    <Modal
      open={templateModalOpen}
      onCancel={() => setTemplateModalOpen(false)}
      footer={null}
      width={900}
      destroyOnClose
      title={null}
    >
      <div className="bsc-tmpl-modal">
        <h2 className="bsc-tmpl-title">{t("modals.chooseTemplate")}</h2>
        <p className="bsc-tmpl-sub">{t("messages.templateDescription")}</p>
        {templatesLoading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin />
          </div>
        ) : (
          <>
            <div className="bsc-tmpl-grid">
              {templates.map((t) => {
                const iconMap: Record<string, React.ReactNode> = {
                  __kaplan__: <AppstoreOutlined />,
                  __ngo__: <TeamOutlined />,
                  __healthcare__: <HeartOutlined />,
                  __education__: <ReadOutlined />,
                  __research__: <ExperimentOutlined />,
                };
                const icon = iconMap[t.id] ?? <AppstoreOutlined />;
                return (
                  <button
                    key={t.id}
                    className={`bsc-tmpl-card ${selectedTemplate === t.id ? "bsc-tmpl-card--selected" : ""}`}
                    onClick={() => setSelectedTemplate(t.id)}
                  >
                    <div className="bsc-tmpl-icon">{icon}</div>
                    <div className="bsc-tmpl-card-name">{t.name}</div>
                    <div className="bsc-tmpl-card-desc">{t.best_for}</div>
                    {selectedTemplate === t.id && (
                      <div className="bsc-tmpl-check">
                        <CheckOutlined />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {selectedTemplate && (
              <div style={{ marginTop: "20px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: 600 }}>
                  Scorecard Title *
                </label>
                <Input
                  placeholder="Enter a name for this scorecard"
                  value={templateTitle}
                  onChange={(e) => {
                    setTemplateTitle(e.target.value);
                    if (e.target.value.trim()) {
                      setTemplateTitleError("");
                    }
                  }}
                  onPressEnter={handleUseTemplate}
                  status={templateTitleError ? "error" : undefined}
                />
                {templateTitleError && (
                  <p style={{ color: "#dc2626", fontSize: "12px", marginTop: "4px" }}>
                    {templateTitleError}
                  </p>
                )}
              </div>
            )}
          </>
        )}
        <div className="bsc-tmpl-actions">
          <Button
            className="bsc-btn-cancel"
            onClick={() => setTemplateModalOpen(false)}
            disabled={creatingScorecard}
          >
            {t("buttons.cancel")}
          </Button>
          <Button
            className="bsc-btn-save"
            onClick={handleUseTemplate}
            loading={creatingScorecard}
            disabled={!selectedTemplate || !templateTitle.trim() || creatingScorecard}
          >
            {t("buttons.useSelectedTemplate")}
          </Button>
        </div>
      </div>
    </Modal>
  );

  // Indicators that block publish while the approval toggle is on.
  // Mirrors the server rule: every indicator must be "accepted".
  const getBlockingIndicators = () => {
    if (!scorecard?.require_approval_before_publishing) return [];
    return scorecard.domains.flatMap((d) =>
      d.indicators
        .filter((i) => i.approval_status !== "accepted")
        .map((i) => ({
          id: i.id,
          name: i.name,
          domainName: d.name,
          status: i.approval_status,
        })),
    );
  };

  const renderPublishModal = () => {
    const blocking = getBlockingIndicators();
    return (
    <Modal
      open={publishModalOpen}
      onCancel={() => setPublishModalOpen(false)}
      footer={null}
      width={440}
      centered
      destroyOnClose
      title={null}
    >
      <div className="bsc-publish-modal">
        <h3 className="bsc-publish-title">{t("modals.publishScorecard")}</h3>
        <p className="bsc-publish-desc">{t("messages.publishDescription")}</p>
        {scorecard && (
          <div className="bsc-publish-snapshot">
            <div className="bsc-publish-snapshot-date">
              {dayjs().format("MMM D, YYYY")} — New
            </div>
            <div className="bsc-publish-snapshot-meta">
              <div>
                <span className="bsc-publish-meta-label">
                  {t("table.domain")}
                </span>
                <span className="bsc-publish-meta-val">
                  {scorecard.domains.length}
                </span>
              </div>
              <div>
                <span className="bsc-publish-meta-label">
                  {t("table.indicators")}
                </span>
                <span className="bsc-publish-meta-val">
                  {scorecard.summary.total}
                </span>
              </div>
              <div>
                <span className="bsc-publish-meta-label">
                  {t("publishSnapshot.overallScore")}
                </span>
                <span
                  className="bsc-publish-meta-val"
                  style={{ color: scoreColor(scorecard.overall_score) }}
                >
                  {scorecard.overall_score !== null
                    ? `${scorecard.overall_score}`
                    : "—"}
                </span>
              </div>
            </div>
          </div>
        )}
        {blocking.length > 0 && (
          <div className="bsc-publish-blocking">
            <div className="bsc-publish-blocking-title">
              {t("publishing.pendingListTitle", { count: blocking.length })}
            </div>
            <p className="bsc-publish-blocking-desc">
              {t("publishing.publishBlocked")}
            </p>
            <ul className="bsc-publish-blocking-list">
              {blocking.map((b) => (
                <li key={b.id}>
                  <span className="bsc-publish-blocking-name">{b.name}</span>
                  <span className="bsc-publish-blocking-domain">
                    {b.domainName}
                  </span>
                  <span
                    className={`bsc-publish-blocking-status bsc-publish-blocking-status--${b.status}`}
                  >
                    {b.status === "rejected"
                      ? t("publishing.statusRejected")
                      : t("publishing.statusPending")}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* <div className="bsc-publish-pin">
          <Checkbox
            checked={pinToDashboard}
            onChange={(e) => setPinToDashboard(e.target.checked)}
          >
            {t("modals.pinCheckboxLabel")}
          </Checkbox>
        </div> */}
        <div className="bsc-publish-actions">
          <button
            className="bsc-btn-cancel"
            onClick={() => setPublishModalOpen(false)}
            disabled={publishingScorecard}
          >
            {t("buttons.cancel")}
          </button>
          <button
            className="bsc-btn-save"
            onClick={() =>
              gate.requirePaid(handlePublish, t("messages.publishDescription"))
            }
            disabled={publishingScorecard || blocking.length > 0}
          >
            {publishingScorecard ? <Spin size="small" /> : null} {t("buttons.publish")}
          </button>
        </div>
      </div>
    </Modal>
    );
  };

  const renderDomainModal = () => {
    const usedWeight =
      scorecard?.domains
        .filter((d) => d.id !== domainModal.id)
        .reduce((s, d) => s + Number(d.weight), 0) ?? 0;
    const remaining = 100 - usedWeight - domainModal.weight;
    return (
      <Modal
        open={domainModal.open}
        onCancel={() => setDomainModal((d) => ({ ...d, open: false }))}
        footer={null}
        title={
          domainModal.mode === "add"
            ? t("modals.addDomain")
            : t("modals.editDomain")
        }
        width={420}
        centered
        destroyOnClose
      >
        <div className="bsc-modal-body">
          <div className="bsc-modal-field">
            <label>{t("forms.name")}</label>
            <Input
              value={domainModal.name}
              onChange={(e) =>
                setDomainModal((d) => ({ ...d, name: e.target.value }))
              }
              placeholder={t("common.enterName")}
              autoFocus
            />
          </div>
          <div className="bsc-modal-field">
            <label>{t("forms.weight")}</label>
            <InputNumber
              value={domainModal.weight}
              onChange={(v) =>
                setDomainModal((d) => ({ ...d, weight: v ?? 0 }))
              }
              min={0}
              max={100}
              style={{ width: "100%" }}
              placeholder="0"
            />
            <span className="bsc-weight-helper">
              {remaining >= 0 ? (
                <>
                  {remaining}% {t("common.weightLeft")}
                </>
              ) : (
                <span style={{ color: "#dc2626" }}>
                  {t("common.exceedsBy", { value: Math.abs(remaining) })}
                </span>
              )}
            </span>
          </div>
          <div className="bsc-modal-actions" style={{ marginTop: 8 }}>
            <button
              className="bsc-btn-cancel"
              onClick={() => setDomainModal((d) => ({ ...d, open: false }))}
            >
              {t("buttons.cancel")}
            </button>
            <button
              className="bsc-btn-save"
              onClick={saveDomain}
              disabled={!domainModal.name.trim() || savingDomain}
            >
              {savingDomain ? <Spin size="small" /> : null} {domainModal.mode === "add"
                ? t("buttons.addDomain")
                : t("buttons.save")}
            </button>
          </div>
        </div>
      </Modal>
    );
  };

  const setBreakdownCell = (dimId: string, catId: string, text: string) =>
    setIndicatorModal((i) => ({
      ...i,
      breakdown_values: {
        ...i.breakdown_values,
        [dimId]: { ...(i.breakdown_values[dimId] ?? {}), [catId]: text },
      },
    }));

  // Removing a tag that was saved asks first, because the values go with it.
  const handleBreakdownDimsChange = (next: string[]) => {
    const apply = () =>
      setIndicatorModal((i) => ({ ...i, breakdown_dims: next }));
    const removedSaved = indicatorModal.breakdown_dims
      .filter((id) => !next.includes(id))
      .filter((id) => indicatorModal.original_breakdown_dims.includes(id));
    if (removedSaved.length === 0) {
      apply();
      return;
    }
    const names = removedSaved
      .map((id) => dimensions.find((d) => d.id === id)?.name ?? "")
      .join(", ");
    Modal.confirm({
      title: t("disaggregation.untagTitle"),
      content: t("disaggregation.untagBody", { dimension: names }),
      okText: t("disaggregation.untag"),
      okButtonProps: { danger: true },
      onOk: apply,
    });
  };

  const renderIndicatorModal = () => {
    const domain = scorecard?.domains.find(
      (d) => d.id === indicatorModal.domainId,
    );
    const usedWeight =
      domain?.indicators
        .filter((i) => i.id !== indicatorModal.id)
        .reduce((s, i) => s + Number(i.weight), 0) ?? 0;
    const remainingWeight = 100 - usedWeight - indicatorModal.weight;

    return (
      <Modal
        open={indicatorModal.open}
        onCancel={() => setIndicatorModal((i) => ({ ...i, open: false }))}
        footer={null}
        title={
          indicatorModal.mode === "add"
            ? t("modals.addIndicator")
            : t("modals.editIndicator")
        }
        width={600}
        centered
        destroyOnClose
        className="bsc-ind-modal"
      >
        <div className="bsc-modal-body">
          <div className="bsc-modal-field">
            <label>{t("forms.name")}</label>
            <Input
              value={indicatorModal.name}
              onChange={(e) =>
                setIndicatorModal((i) => ({ ...i, name: e.target.value }))
              }
              placeholder={t("common.enterName")}
              autoFocus
            />
          </div>
          <div className="bsc-modal-field">
            <label>
              {t("forms.description")}{" "}
              <span className="bsc-optional">{t("labels.optional")}</span>
            </label>
            <Input.TextArea
              value={indicatorModal.description}
              onChange={(e) =>
                setIndicatorModal((i) => ({
                  ...i,
                  description: e.target.value,
                }))
              }
              rows={2}
              placeholder={t("common.enterDescription")}
            />
          </div>
          <div className="bsc-modal-field">
            <label>{t("forms.weightInDomain")}</label>
            <InputNumber
              value={indicatorModal.weight}
              onChange={(v) =>
                setIndicatorModal((i) => ({ ...i, weight: v ?? 0 }))
              }
              min={0}
              max={100}
              style={{ width: "100%" }}
            />
            <span className="bsc-weight-helper">
              {remainingWeight >= 0 ? (
                <>
                  {remainingWeight}% {t("common.weightLeft")}
                </>
              ) : (
                <span style={{ color: "#dc2626" }}>
                  {t("common.exceedsBy", { value: Math.abs(remainingWeight) })}
                </span>
              )}
            </span>
          </div>

          {/* Value configuration */}
          <div className="bsc-ind-section">
            <div className="bsc-ind-section-title">
              {t("sections.valueConfiguration")}
            </div>
            <div className="bsc-modal-field">
              <label>{t("forms.valueScale")}</label>
              <Select
                value={indicatorModal.value_scale || undefined}
                onChange={(v) =>
                  setIndicatorModal((i) => ({ ...i, value_scale: v }))
                }
                placeholder="Select a scale"
                style={{ width: "100%" }}
                options={getValueScaleOptions(t)}
              />
            </div>
            <div className="bsc-modal-field">
              <label>{t("forms.valueSource")}</label>
              <div className="bsc-source-toggle">
                <button
                  className={`bsc-source-btn ${indicatorModal.value_source === "formula" ? "bsc-source-btn--active" : ""}`}
                  onClick={() =>
                    setIndicatorModal((i) => ({
                      ...i,
                      value_source: "formula",
                    }))
                  }
                >
                  <span>⚡</span> {t("modes.formula")}
                </button>
                <button
                  className={`bsc-source-btn ${indicatorModal.value_source === "manual" ? "bsc-source-btn--active" : ""}`}
                  onClick={() =>
                    setIndicatorModal((i) => ({ ...i, value_source: "manual" }))
                  }
                >
                  ✏ {t("modes.manual")}
                </button>
              </div>
            </div>
            {indicatorModal.value_source === "formula" ? (
              <>
                <div className="bsc-modal-field">
                  <label>{t("forms.linkedFormula")}</label>
                  <Select
                    placeholder={t("forms.selectNamedFormula")}
                    style={{ width: "100%" }}
                    value={indicatorModal.linked_formula || undefined}
                    onChange={(v) =>
                      setIndicatorModal((i) => ({ ...i, linked_formula: v }))
                    }
                    options={linkableFormulas.map((f) => ({
                      value: f.id,
                      label: f.name,
                      disabled: !f.is_named_result,
                    }))}
                    notFoundContent={t("messages.noNamedFormulas")}
                  />
                  <span className="bsc-hint">
                    {t("forms.autoFormulaPulled")}
                  </span>
                </div>
                <div className="bsc-modal-field">
                  <label>{t("forms.updateMode")}</label>
                  <div className="bsc-radio-group">
                    <label className="bsc-radio-label">
                      <input
                        type="radio"
                        checked={indicatorModal.update_mode === "live"}
                        onChange={() =>
                          setIndicatorModal((i) => ({
                            ...i,
                            update_mode: "live",
                          }))
                        }
                      />
                      <div>
                        <strong>{t("modes.live")}</strong>
                        <span>{t("messages.liveUpdateDesc")}</span>
                      </div>
                    </label>
                    <label className="bsc-radio-label">
                      <input
                        type="radio"
                        checked={indicatorModal.update_mode === "snapshot"}
                        onChange={() =>
                          setIndicatorModal((i) => ({
                            ...i,
                            update_mode: "snapshot",
                          }))
                        }
                      />
                      <div>
                        <strong>{t("modes.snapshot")}</strong>
                        <span>{t("messages.snapshotUpdateDesc")}</span>
                      </div>
                    </label>
                  </div>
                </div>
              </>
            ) : (
              <div className="bsc-modal-field">
                <label>
                  {t("forms.currentValue")}{" "}
                  <span className="bsc-optional">{t("labels.optional")}</span>
                </label>
                <Input
                  value={indicatorModal.current_value}
                  onChange={(e) =>
                    setIndicatorModal((i) => ({
                      ...i,
                      current_value: e.target.value,
                    }))
                  }
                  placeholder={t("common.enterValue")}
                />
                <span className="bsc-hint">
                  {t("messages.currentValueHint")}
                </span>
              </div>
            )}
          </div>

          {/* Disaggregation */}
          <div className="bsc-ind-section">
            <div className="bsc-ind-section-header">
              <button
                type="button"
                className="bsc-ind-section-toggle"
                onClick={() => setBreakdownOpen((o) => !o)}
              >
                {breakdownOpen ? "▾" : "▸"} {t("disaggregation.title")}{" "}
                <span className="bsc-optional">{t("labels.optional")}</span>
              </button>
            </div>
            {breakdownOpen && (
              <div className="bsc-disagg-body">
                <p className="bsc-hint">{t("disaggregation.hint")}</p>
                {dimensions.length === 0 ? (
                  <div className="bsc-disagg-empty">
                    <span>{t("disaggregation.noDimensions")}</span>
                    <button
                      type="button"
                      className="bsc-alert-link"
                      onClick={() => setManageDimOpen(true)}
                    >
                      {t("dimensions.toolbar")}
                    </button>
                  </div>
                ) : (
                  <>
                    <Select
                      mode="multiple"
                      className="bsc-disagg-select"
                      placeholder={t("disaggregation.selectPlaceholder")}
                      value={indicatorModal.breakdown_dims}
                      onChange={handleBreakdownDimsChange}
                      options={dimensions.map((d) => ({
                        label: d.name,
                        value: d.id,
                      }))}
                    />
                    {dimensions
                      .filter((d) => indicatorModal.breakdown_dims.includes(d.id))
                      .map((d) => (
                        <div key={d.id} className="bsc-disagg-dim">
                          <div className="bsc-disagg-dim-name">{d.name}</div>
                          <div className="bsc-disagg-grid">
                            {d.categories.map((c) => (
                              <label key={c.id} className="bsc-disagg-cell">
                                <span>{c.name}</span>
                                <Input
                                  value={
                                    indicatorModal.breakdown_values[d.id]?.[c.id] ??
                                    ""
                                  }
                                  placeholder="—"
                                  inputMode="decimal"
                                  status={
                                    parseBreakdownText(
                                      indicatorModal.breakdown_values[d.id]?.[c.id] ?? "",
                                    ) === undefined
                                      ? "error"
                                      : undefined
                                  }
                                  onChange={(e) =>
                                    setBreakdownCell(d.id, c.id, e.target.value)
                                  }
                                />
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Dynamic range */}
          <div className="bsc-ind-section">
            <div className="bsc-ind-section-header">
              <div className="bsc-ind-section-title">
                {t("sections.dynamicRange")}
              </div>
              <div className="bsc-score-count">
                <button
                  className="bsc-count-btn"
                  onClick={() =>
                    setIndicatorModal((i) =>
                      i.score_bands.length > 1
                        ? { ...i, score_bands: i.score_bands.slice(0, -1) }
                        : i,
                    )
                  }
                >
                  −
                </button>
                <span>
                  {indicatorModal.score_bands.length}{" "}
                  {t("common.scoreVariables")}
                </span>
                <button
                  className="bsc-count-btn"
                  onClick={() =>
                    setIndicatorModal((i) =>
                      i.score_bands.length < 5
                        ? {
                            ...i,
                            score_bands: [
                              ...i.score_bands,
                              {
                                color: "#6b7280",
                                from: 0,
                                to: 100,
                                label: "",
                                description: "",
                              },
                            ],
                          }
                        : i,
                    )
                  }
                >
                  +
                </button>
              </div>
            </div>
            <p className="bsc-hint" style={{ marginBottom: 10 }}>
              {t("messages.dynamicRangeHint")}
            </p>
            <div className="bsc-score-bands">
              <div className="bsc-score-bands-header">
                <span style={{ width: 16 }} />
                <span className="bsc-band-col">{t("forms.fromLabel")}</span>
                <span className="bsc-band-col">{t("forms.toLabel")}</span>
                <span className="bsc-band-col bsc-band-col--wide">
                  {t("forms.scoreLabelHeader")}
                </span>
                <span className="bsc-band-col bsc-band-col--wide">
                  {t("forms.scoreDescriptionHeader")}
                </span>
                <span style={{ width: 24 }} />
              </div>
              {indicatorModal.score_bands.map((band, idx) => (
                <div key={idx} className="bsc-score-band-row">
                  <span
                    className="bsc-band-color-dot"
                    style={{ background: band.color }}
                  />
                  <InputNumber
                    value={band.from}
                    min={0}
                    max={100}
                    onChange={(v) =>
                      setIndicatorModal((i) => {
                        const b = [...i.score_bands];
                        b[idx] = { ...b[idx], from: v ?? 0 };
                        return { ...i, score_bands: b };
                      })
                    }
                    className="bsc-band-input"
                  />
                  <span className="bsc-band-sep">%</span>
                  <InputNumber
                    value={band.to}
                    min={0}
                    max={100}
                    onChange={(v) =>
                      setIndicatorModal((i) => {
                        const b = [...i.score_bands];
                        b[idx] = { ...b[idx], to: v ?? 0 };
                        return { ...i, score_bands: b };
                      })
                    }
                    className="bsc-band-input"
                  />
                  <span className="bsc-band-sep">%</span>
                  <Input
                    value={band.label}
                    onChange={(e) =>
                      setIndicatorModal((i) => {
                        const b = [...i.score_bands];
                        b[idx] = { ...b[idx], label: e.target.value };
                        return { ...i, score_bands: b };
                      })
                    }
                    placeholder={t("forms.scoreLabelPlaceholder")}
                    className="bsc-band-label"
                  />
                  <Input
                    value={band.description ?? ""}
                    onChange={(e) =>
                      setIndicatorModal((i) => {
                        const b = [...i.score_bands];
                        b[idx] = { ...b[idx], description: e.target.value };
                        return { ...i, score_bands: b };
                      })
                    }
                    placeholder={t("forms.scoreDescriptionPlaceholder")}
                    className="bsc-band-desc"
                  />
                  <button
                    className="bsc-band-remove"
                    onClick={() =>
                      setIndicatorModal((i) => ({
                        ...i,
                        score_bands: i.score_bands.filter((_, j) => j !== idx),
                      }))
                    }
                  >
                    −
                  </button>
                </div>
              ))}
            </div>
            <div className="bsc-apply-domain">
              <Checkbox
                checked={indicatorModal.apply_to_domain}
                onChange={(e) =>
                  setIndicatorModal((i) => ({
                    ...i,
                    apply_to_domain: e.target.checked,
                  }))
                }
              >
                {t("forms.applyToDomain")}
              </Checkbox>
            </div>
          </div>

          {/* Benchmark configuration */}
          <div className="bsc-ind-section">
            <div className="bsc-ind-section-title">
              {t("sections.benchmarkConfiguration")}
            </div>
            <div className="bsc-modal-field">
              <label>{t("forms.benchmarkSource")}</label>
              <div className="bsc-source-toggle">
                <button
                  className={`bsc-source-btn ${indicatorModal.benchmark_source === "library" ? "bsc-source-btn--active" : ""}`}
                  onClick={() =>
                    setIndicatorModal((i) => ({
                      ...i,
                      benchmark_source: "library",
                    }))
                  }
                >
                  📚 {t("forms.selectBenchmarkLibrary")}
                </button>
                <button
                  className={`bsc-source-btn ${indicatorModal.benchmark_source === "manual" ? "bsc-source-btn--active" : ""}`}
                  onClick={() =>
                    setIndicatorModal((i) => ({
                      ...i,
                      benchmark_source: "manual",
                    }))
                  }
                >
                  ✏ {t("modes.manual")}
                </button>
              </div>
            </div>
            {indicatorModal.benchmark_source === "library" ? (
              <div className="bsc-modal-field">
                <label>{t("forms.selectBenchmarkLibrary")}</label>
                <Select
                  placeholder={t("forms.selectBenchmarkLibrary")}
                  style={{ width: "100%" }}
                  options={benchmarkLibrary.map((b) => ({
                    value: b.id,
                    label: b.name,
                  }))}
                  notFoundContent={t("messages.noBenchmarkLibraryEntries")}
                />
                <span className="bsc-hint">
                  {t("forms.pullsFromBenchmark")}
                </span>
              </div>
            ) : (
              <>
                <p className="bsc-hint">
                  {t("forms.valuesConfiguredManually")}
                </p>
                <div className="bsc-benchmark-row">
                  <div className="bsc-modal-field">
                    <label>{t("forms.lowerThreshold")}</label>
                    <InputNumber
                      value={indicatorModal.lower_threshold}
                      onChange={(v) =>
                        setIndicatorModal((i) => ({
                          ...i,
                          lower_threshold: v,
                        }))
                      }
                      min={0}
                      max={100}
                      style={{ width: "100%" }}
                      placeholder="0"
                    />
                  </div>
                  <div className="bsc-modal-field">
                    <label>{t("forms.upperThreshold")}</label>
                    <InputNumber
                      value={indicatorModal.upper_threshold}
                      onChange={(v) =>
                        setIndicatorModal((i) => ({
                          ...i,
                          upper_threshold: v,
                        }))
                      }
                      min={0}
                      max={100}
                      style={{ width: "100%" }}
                      placeholder="0"
                    />
                  </div>
                  <div className="bsc-modal-field">
                    <label>{t("forms.target")}</label>
                    <InputNumber
                      value={indicatorModal.target}
                      onChange={(v) =>
                        setIndicatorModal((i) => ({ ...i, target: v }))
                      }
                      min={0}
                      style={{ width: "100%" }}
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="bsc-apply-domain">
                  <Checkbox>{t("forms.applyToDomain")}</Checkbox>
                </div>
              </>
            )}
          </div>

          <div className="bsc-modal-actions">
            <button
              className="bsc-btn-cancel"
              onClick={() => setIndicatorModal((i) => ({ ...i, open: false }))}
            >
              {t("buttons.cancel")}
            </button>
            <button
              className="bsc-btn-save"
              onClick={saveIndicator}
              disabled={!indicatorModal.name.trim() || savingIndicator}
            >
              {savingIndicator ? <Spin size="small" /> : null} {indicatorModal.mode === "add"
                ? t("buttons.addIndicator")
                : t("buttons.save")}
            </button>
          </div>
        </div>
      </Modal>
    );
  };

  const renderApprovalModal = () => {
    if (!approvalModal) return null;
    const isApprove = approvalModal.status === "accepted";
    return (
      <Modal
        open={approvalModal.open}
        onCancel={() => setApprovalModal(null)}
        footer={null}
        width={440}
        centered
        destroyOnClose
        title={null}
      >
        <div className="bsc-approval-modal">
          <div
            className="bsc-approval-icon"
            style={{
              background: isApprove ? "#dcfce7" : "#fee2e2",
              color: isApprove ? "#16a34a" : "#dc2626",
            }}
          >
            {isApprove ? <CheckOutlined /> : <ExclamationCircleOutlined />}
          </div>
          <h3 className="bsc-approval-title">
            {isApprove
              ? t("modals.approveIndicator")
              : t("modals.rejectIndicator")}
          </h3>
          <p className="bsc-approval-ind-name">
            "{approvalModal.indicatorName}"
          </p>
          <p className="bsc-approval-desc">
            {isApprove
              ? t("modals.approveDescription")
              : t("modals.rejectDescription")}
          </p>
          <div className="bsc-modal-field">
            <label>
              {t("modals.noteLabel")}{" "}
              <span className="bsc-optional">{t("labels.optional")}</span>
            </label>
            <Input.TextArea
              value={approvalModal.note}
              onChange={(e) =>
                setApprovalModal((prev) =>
                  prev ? { ...prev, note: e.target.value } : prev,
                )
              }
              rows={3}
              placeholder={
                isApprove
                  ? t("modals.verificationNote")
                  : t("modals.rejectionNote")
              }
              autoFocus
            />
          </div>
          <div className="bsc-approval-actions">
            <button
              className="bsc-btn-cancel"
              onClick={() => setApprovalModal(null)}
            >
              {t("buttons.cancel")}
            </button>
            <button
              className={isApprove ? "bsc-btn-approve" : "bsc-btn-reject"}
              onClick={handleApproval}
              disabled={approvingIndicator}
            >
              {approvingIndicator ? <Spin size="small" /> : null} {isApprove ? t("buttons.approve") : t("buttons.reject")}
            </button>
          </div>
        </div>
      </Modal>
    );
  };

  const renderUnsavedChangesModal = () => (
    <Modal
      open={unsavedChangesModal}
      onCancel={() => setUnsavedChangesModal(false)}
      footer={null}
      width={400}
      centered
      destroyOnClose
    >
      <div className="bsc-delete-modal">
        <div
          className="bsc-delete-icon"
          style={{ background: "#fff7ed", color: "#d97706" }}
        >
          ⚠
        </div>
        <h3 className="bsc-delete-title">{t("modals.unsavedChanges")}</h3>
        <p className="bsc-delete-text">{t("messages.unsavedChangesWarning")}</p>
        <div className="bsc-delete-actions">
          <button
            className="bsc-btn-cancel"
            onClick={() => setUnsavedChangesModal(false)}
          >
            {t("modals.leaveWithoutSaving")}
          </button>
          <button
            className="bsc-btn-save"
            onClick={() => {
              setUnsavedChangesModal(false);
              toast.success(t("messages.changesSaved"));
            }}
          >
            {t("buttons.saveChanges")}
          </button>
        </div>
      </div>
    </Modal>
  );

  const renderDeleteModal = () => (
    <Modal
      open={!!deleteTarget}
      onCancel={() => setDeleteTarget(null)}
      footer={null}
      width={400}
      centered
      destroyOnClose
    >
      <div className="bsc-delete-modal">
        <div className="bsc-delete-icon">
          <ExclamationCircleOutlined />
        </div>
        <h3 className="bsc-delete-title">
          {t("modals.deleteTitle", {
            type:
              deleteTarget?.type === "domain"
                ? t("labels.domain")
                : t("labels.indicator"),
          })}
        </h3>
        <p className="bsc-delete-text">
          <strong>"{deleteTarget?.label}"</strong>{" "}
          {t("messages.deleteWarning", {
            context: deleteTarget?.type === "domain" ? "domain" : "indicator",
          })}
        </p>
        <div className="bsc-delete-actions">
          <button
            className="bsc-btn-cancel"
            onClick={() => setDeleteTarget(null)}
          >
            {t("buttons.cancel")}
          </button>
          <button
            className="bsc-btn-delete"
            onClick={confirmDelete}
            disabled={deletingItem}
          >
            {deletingItem ? <Spin size="small" /> : null} {t("buttons.delete")}
          </button>
        </div>
      </div>
    </Modal>
  );

  // ── Main ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="bsc-page">
        <div className="bsc-card" style={{ padding: 60, textAlign: "center" }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="bsc-page">
      <div className="bsc-card">
        {!scorecard ? (
          renderEmpty()
        ) : (
          <>
            <div className="bsc-tabs">
              <button
                className={`bsc-tab ${activeTab === "scorecard" ? "bsc-tab--active" : ""}`}
                onClick={() => setActiveTab("scorecard")}
              >
                {t("tabs.scorecard")}
              </button>
              <button
                className={`bsc-tab ${activeTab === "indicators" ? "bsc-tab--active" : ""}`}
                onClick={() => setActiveTab("indicators")}
              >
                {t("tabs.indicators")}
                {scorecard && scorecard.summary.total > 0 && (
                  <span className="bsc-tab-count">
                    {scorecard.summary.total}
                  </span>
                )}
              </button>
              <button
                className={`bsc-tab ${activeTab === "benchmarks" ? "bsc-tab--active" : ""}`}
                onClick={() => setActiveTab("benchmarks")}
              >
                {t("tabs.benchmarks")}
              </button>
              <button
                className={`bsc-tab ${activeTab === "history" ? "bsc-tab--active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                {t("tabs.history")}
              </button>
            </div>
            {activeTab === "scorecard" && renderScorecardTab()}
            {activeTab === "indicators" && renderIndicatorsTab()}
            {activeTab === "benchmarks" && renderBenchmarksTab()}
            {activeTab === "history" && renderHistoryTab()}
          </>
        )}
      </div>
      {renderTemplateModal()}
      {renderPublishModal()}
      {renderDomainModal()}
      {scorecard && (
        <ManageDimensionsModal
          open={manageDimOpen}
          scorecardId={scorecard.id}
          dimensions={dimensions}
          canManage={canManageUsers(getCurrentRole())}
          onClose={() => setManageDimOpen(false)}
          onChanged={loadDimensions}
        />
      )}
      {scorecard && (
        <BulkEntryModal
          open={bulkOpen}
          scorecardId={scorecard.id}
          dimensions={dimensions}
          initialDimensionId={dimensions[0]?.id ?? null}
          canManage={canManageUsers(getCurrentRole())}
          onClose={() => setBulkOpen(false)}
          onSaved={() => {
            loadDimensions();
            setBreakdownRefresh((n) => n + 1);
          }}
        />
      )}
      {scorecard && (
        <ScorecardSettingsModal
          open={settingsOpen}
          scorecard={scorecard}
          canManage={canManageUsers(getCurrentRole())}
          onClose={() => setSettingsOpen(false)}
          onChanged={loadScorecard}
        />
      )}
      {renderIndicatorModal()}
      {renderApprovalModal()}
      {renderDeleteModal()}
      {renderUnsavedChangesModal()}
      {scorecard && (
        <PublicBalancedScorecardHistoryShareModal
          open={shareModalOpen}
          scorecardId={scorecard.id}
          onClose={() => setShareModalOpen(false)}
        />
      )}
    </div>
  );
};

export default BalancedScorecardPage;
